import React, { useState, useEffect, useRef } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { usersAPI, uploadAPI } from '../services/api'
import { User, LogOut, Star, X, Edit, Save, Folder, BarChart3, Camera, Plus, Trash2, ExternalLink, Instagram, Youtube, Video, Eye, Upload, CreditCard, Receipt, Building, Users, Globe, Linkedin, CheckCircle, AlertCircle, Package, Clock, RotateCcw, Sparkles, Music, FileText, Film, Type, Mic, Image, HelpCircle, MapPin, ArrowLeft } from 'lucide-react'
import PayoutSetup from '../components/PayoutSetup'
import BillingSettings from '../components/BillingSettings'
import PackagesTour from '../components/PackagesTour'
import ProfileTour from '../components/ProfileTour'

// initialTab: gebruikt wanneer ProfilePage binnen het dashboard hangt — daar zijn
// geen query params, dus kan het tabblad niet via ?tab= gekozen worden.
const ProfilePage = ({ initialTab } = {}) => {
  const { user, logout, updateUser, refreshUser } = useAuth()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [activeTab, setActiveTab] = useState(initialTab || searchParams.get('tab') || 'profile')
  const packagesTourRef = useRef(null)
  const profileTourRef = useRef(null)
  const [isEditing, setIsEditing] = useState(false)
  const [loading, setLoading] = useState(false)
  const [saveSuccess, setSaveSuccess] = useState(false)
  const [saveError, setSaveError] = useState('')
  const [uploadingProfilePic, setUploadingProfilePic] = useState(false)
  const [showPortfolioModal, setShowPortfolioModal] = useState(false)
  const [editingPortfolioItem, setEditingPortfolioItem] = useState(null)
  const [portfolioFormData, setPortfolioFormData] = useState({
    title: '',
    description: '',
    videoUrl: '',
    thumbnailUrl: '',
    category: 'other'
  })
  const [uploadingMoodboard, setUploadingMoodboard] = useState(false)
  const [moodboardCaption, setMoodboardCaption] = useState('')
  const [editingMoodboardItem, setEditingMoodboardItem] = useState(null)
  const [editingCaption, setEditingCaption] = useState('')
  const [uploadingVideo, setUploadingVideo] = useState(false)
  const [uploadingThumbnail, setUploadingThumbnail] = useState(false)
  const [uploadingCompanyLogo, setUploadingCompanyLogo] = useState(false)
  const [uploadingAgencyLogo, setUploadingAgencyLogo] = useState(false)
  const [uploadProgress, setUploadProgress] = useState(0)
  const [newSpecialization, setNewSpecialization] = useState('')
  const [showBrandForm, setShowBrandForm] = useState(false)
  const [editingBrandIndex, setEditingBrandIndex] = useState(null)
  const [brandFormData, setBrandFormData] = useState({ name: '', logo: '', website: '' })
  const [showTeamForm, setShowTeamForm] = useState(false)
  const [editingTeamIndex, setEditingTeamIndex] = useState(null)
  const [teamFormData, setTeamFormData] = useState({ name: '', email: '', role: 'member' })
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    displayName: '',
    bio: '',
    profilePictureUrl: '',
    location: { city: '', country: 'Netherlands' },
    niches: [],
    dateOfBirth: '',
    pricing: { baseRate: '', negotiable: true },
    socialMedia: {
      instagram: { username: '', followers: '' },
      tiktok: { username: '', followers: '' },
      youtube: { username: '', subscribers: '' }
    },
    companyName: '',
    contactPerson: '',
    companySize: '',
    website: '',
    companyBio: '',
    industry: '',
    specializations: [],
    companyLogo: '',
    // Packages
    packages: [],
    // Agency fields
    agencyName: '',
    agencyBio: '',
    agencyLogo: '',
    agencySize: '',
    managedBrands: [],
    teamMembers: [],
  })


  useEffect(() => {
    if (user) {
      setFormData({
        firstName: user.firstName || '',
        lastName: user.lastName || '',
        displayName: user.displayName || '',
        bio: user.bio || '',
        profilePictureUrl: user.profilePictureUrl || '',
        location: user.location || { city: '', country: 'Netherlands' },
        niches: user.niches || [],
        dateOfBirth: user.dateOfBirth ? user.dateOfBirth.substring(0, 10) : '',
        pricing: user.pricing || { baseRate: '', negotiable: true },
        socialMedia: user.socialMedia || {
          instagram: { username: '', followers: '' },
          tiktok: { username: '', followers: '' },
          youtube: { username: '', subscribers: '' }
        },
        companyName: user.companyName || '',
        contactPerson: user.contactPerson || '',
        companySize: user.companySize || '',
        website: user.website || '',
        companyBio: user.companyBio || '',
        industry: user.industry || '',
        specializations: user.specializations || [],
        companyLogo: user.companyLogo || '',
        packages: user.packages || [],
        agencyName: user.agencyName || '',
        agencyBio: user.agencyBio || '',
        agencyLogo: user.agencyLogo || '',
        agencySize: user.agencySize || '',
        managedBrands: user.managedBrands || [],
        teamMembers: user.teamMembers || [],
      })
    }
  }, [user])

  // Body-scroll-lock zolang de portfolio-modal open is (achtergrond scrolt anders mee op touch).
  useEffect(() => {
    if (!showPortfolioModal) return
    const previous = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => { document.body.style.overflow = previous }
  }, [showPortfolioModal])

  const handleLogout = () => {
    logout()
    navigate('/')
  }

  const handleInputChange = (path, value) => {
    setFormData(prev => {
      const keys = path.split('.')
      // Deep clone to avoid mutating nested objects
      const newData = JSON.parse(JSON.stringify(prev))
      let current = newData

      for (let i = 0; i < keys.length - 1; i++) {
        if (!current[keys[i]]) current[keys[i]] = {}
        current = current[keys[i]]
      }

      current[keys[keys.length - 1]] = value
      return newData
    })
  }

  const handleNicheToggle = (niche) => {
    setFormData(prev => ({
      ...prev,
      niches: prev.niches.includes(niche)
        ? prev.niches.filter(n => n !== niche)
        : [...prev.niches, niche]
    }))
  }

  const handleSave = async () => {
    // Pakket zonder naam werd door de backend geweigerd met een Engelse
    // Mongoose-melding; creators bleven dan eindeloos op "Opslaan" drukken.
    const leegPakket = (formData.packages || []).findIndex(p => !p?.name || !String(p.name).trim())
    if (leegPakket !== -1) {
      setSaveError(`Pakket ${leegPakket + 1} heeft nog geen naam. Geef het een naam (bijv. Basis, Standaard of Premium) of verwijder het lege pakket.`)
      setActiveTab('packages')
      window.scrollTo({ top: 0, behavior: 'smooth' })
      return
    }
    try {
      setLoading(true)
      setSaveError('')

      // Clean up data before sending - convert empty strings to proper types for Number fields
      const cleanedData = { ...formData }
      if (cleanedData.socialMedia) {
        cleanedData.socialMedia = {
          instagram: {
            username: cleanedData.socialMedia.instagram?.username || '',
            followers: parseInt(cleanedData.socialMedia.instagram?.followers) || 0
          },
          tiktok: {
            username: cleanedData.socialMedia.tiktok?.username || '',
            followers: parseInt(cleanedData.socialMedia.tiktok?.followers) || 0
          },
          youtube: {
            username: cleanedData.socialMedia.youtube?.username || '',
            subscribers: parseInt(cleanedData.socialMedia.youtube?.subscribers) || 0
          }
        }
      }
      if (cleanedData.pricing) {
        cleanedData.pricing = {
          ...cleanedData.pricing,
          baseRate: parseInt(cleanedData.pricing.baseRate) || 25
        }
      }

      await usersAPI.updateProfile(cleanedData)
      await refreshUser()
      setIsEditing(false)
      setSaveSuccess(true)
      window.scrollTo({ top: 0, behavior: 'smooth' })
      setTimeout(() => setSaveSuccess(false), 3000)
    } catch (error) {
      console.error('Error updating profile:', error)
      // Validatiefouten zitten in data.messages (array); data.error is een generieke Engelse kop
      const serverDetails = error.response?.data?.messages
      const serverMsg = (Array.isArray(serverDetails) && serverDetails.length ? serverDetails.join(' • ') : null)
        || error.response?.data?.message
      if (error.code === 'ECONNABORTED') {
        setSaveError('Verbinding verlopen. Controleer je internetverbinding en probeer het opnieuw.')
      } else if (error.response?.status === 400) {
        setSaveError(serverMsg || 'Ongeldige gegevens. Controleer je invoer en probeer het opnieuw.')
      } else if (error.response?.status === 413) {
        setSaveError('Data te groot. Verklein je invoer en probeer het opnieuw.')
      } else {
        setSaveError(serverMsg || 'Er is iets misgegaan bij het opslaan. Probeer het opnieuw.')
      }
      window.scrollTo({ top: 0, behavior: 'smooth' })
      setTimeout(() => setSaveError(''), 5000)
    } finally {
      setLoading(false)
    }
  }

  const handleProfilePictureUpload = async (event) => {
    const file = event.target.files[0]
    if (!file) return

    if (file.size > 5 * 1024 * 1024) {
      alert('Bestand is te groot. Maximum 5MB toegestaan.')
      return
    }

    try {
      setUploadingProfilePic(true)
      setUploadProgress(0)
      const response = await uploadAPI.uploadProfilePicture(file, (e) => {
        if (e.total) setUploadProgress(Math.round((e.loaded / e.total) * 100))
      })
      await refreshUser()
    } catch (error) {
      console.error('Error uploading profile picture:', error)
      const msg = error.response?.data?.message || ''
      if (error.code === 'ECONNABORTED') {
        alert('Upload timeout - je verbinding is te langzaam. Probeer een kleinere foto.')
      } else if (msg.includes('too large') || msg.includes('File too large')) {
        alert('Profielfoto is te groot. Maximum 5MB toegestaan.')
      } else {
        alert(msg || 'Fout bij uploaden profielfoto. Probeer het opnieuw.')
      }
    } finally {
      setUploadingProfilePic(false)
    }
  }

  const handlePortfolioSubmit = async (e) => {
    e.preventDefault()

    // Alleen downloadbare uploads of embed-bare video's (YouTube) toestaan.
    // Links naar externe socials (Instagram/TikTok e.d.) zijn niet toegestaan,
    // zodat bedrijven creators niet buiten UGC4you om kunnen benaderen.
    const url = (portfolioFormData.videoUrl || '').trim()
    const isUpload = /\/uploads\//i.test(url)
    const isYouTube = /(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)/i.test(url)
    const isVideoFile = /\.(mp4|webm|mov|m4v|ogv)(\?|#|$)/i.test(url)
    if (!isUpload && !isYouTube && !isVideoFile) {
      alert('Externe links werken niet. Upload je foto\'s of video\'s als bestand.')
      return
    }

    try {
      setLoading(true)
      if (editingPortfolioItem) {
        await usersAPI.updatePortfolioItem(editingPortfolioItem._id, portfolioFormData)
      } else {
        await usersAPI.addPortfolioItem(portfolioFormData)
      }
      await refreshUser()
      setShowPortfolioModal(false)
      setEditingPortfolioItem(null)
      setPortfolioFormData({
        title: '',
        description: '',
        videoUrl: '',
        thumbnailUrl: '',
        category: 'other'
      })
    } catch (error) {
      console.error('Error with portfolio item:', error)
      alert('Fout bij opslaan portfolio item')
    } finally {
      setLoading(false)
    }
  }

  const handlePortfolioDelete = async (itemId) => {
    if (!confirm('Weet je zeker dat je dit portfolio item wilt verwijderen?')) return

    try {
      await usersAPI.removePortfolioItem(itemId)
      await refreshUser()
    } catch (error) {
      console.error('Error deleting portfolio item:', error)
      alert('Fout bij verwijderen portfolio item')
    }
  }

  const handleEditPortfolioItem = (item) => {
    setEditingPortfolioItem(item)
    setPortfolioFormData({
      title: item.title,
      description: item.description || '',
      videoUrl: item.videoUrl,
      thumbnailUrl: item.thumbnailUrl || '',
      category: item.category
    })
    setShowPortfolioModal(true)
  }

  const handleMoodboardUpload = async (event) => {
    const files = Array.from(event.target.files)
    if (!files.length) return

    const currentCount = user.moodboard?.length || 0
    const maxAllowed = 12 - currentCount
    if (files.length > maxAllowed) {
      alert(`Je kunt nog maximaal ${maxAllowed} foto's toevoegen (12 max)`)
      return
    }

    for (const file of files) {
      if (file.size > 5 * 1024 * 1024) {
        alert(`${file.name} is te groot. Maximum 5MB per foto.`)
        return
      }
    }

    try {
      setUploadingMoodboard(true)
      setUploadProgress(0)
      for (const file of files) {
        const uploadResponse = await uploadAPI.uploadImage(file, (e) => {
          if (e.total) setUploadProgress(Math.round((e.loaded / e.total) * 100))
        })
        const imageUrl = uploadResponse.data.file.url
        await usersAPI.addMoodboardItem({ imageUrl, caption: '' })
      }
      await refreshUser()
    } catch (error) {
      console.error('Error uploading moodboard image:', error)
      const msg = error.response?.data?.message || ''
      if (error.code === 'ECONNABORTED') {
        alert('Upload timeout - probeer een kleinere afbeelding.')
      } else if (msg.includes('too large') || msg.includes('File too large')) {
        alert('Afbeelding is te groot. Maximum 5MB per foto.')
      } else {
        alert(msg || 'Fout bij uploaden moodboard foto. Probeer het opnieuw.')
      }
    } finally {
      setUploadingMoodboard(false)
      event.target.value = ''
    }
  }

  const handleMoodboardDelete = async (itemId) => {
    if (!confirm('Weet je zeker dat je deze foto wilt verwijderen?')) return
    try {
      await usersAPI.removeMoodboardItem(itemId)
      await refreshUser()
    } catch (error) {
      console.error('Error deleting moodboard item:', error)
      alert('Fout bij verwijderen moodboard foto')
    }
  }

  const handleMoodboardCaptionSave = async (itemId) => {
    try {
      await usersAPI.updateMoodboardItem(itemId, { caption: editingCaption })
      setEditingMoodboardItem(null)
      setEditingCaption('')
      await refreshUser()
    } catch (error) {
      console.error('Error updating moodboard caption:', error)
      alert('Fout bij opslaan bijschrift')
    }
  }

  const handleSocialMediaSave = async () => {
    try {
      setLoading(true)
      await usersAPI.updateSocialMedia(formData.socialMedia)
      await refreshUser()
    } catch (error) {
      console.error('Error updating social media:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleVideoUpload = async (event) => {
    const file = event.target.files[0]
    if (!file) return

    if (file.size > 50 * 1024 * 1024) {
      alert('Video is te groot. Maximum 50MB toegestaan.')
      return
    }

    try {
      setUploadingVideo(true)
      setUploadProgress(0)
      const response = await uploadAPI.uploadVideo(file, (e) => {
        if (e.total) setUploadProgress(Math.round((e.loaded / e.total) * 100))
      })
      setPortfolioFormData(prev => ({
        ...prev,
        videoUrl: response.data.file.url
      }))
    } catch (error) {
      console.error('Error uploading video:', error)
      const msg = error.response?.data?.message || ''
      if (error.code === 'ECONNABORTED') {
        alert('Upload timeout - de video is mogelijk te groot of je verbinding te langzaam. Probeer een kleiner bestand (max 50MB).')
      } else if (msg.includes('too large') || msg.includes('File too large')) {
        alert('Video is te groot. Maximum 50MB toegestaan.')
      } else if (msg.includes('Only video')) {
        alert('Ongeldig bestandstype. Alleen video bestanden (MP4, MOV, AVI) zijn toegestaan.')
      } else {
        alert(msg || 'Fout bij uploaden video. Probeer het opnieuw.')
      }
    } finally {
      setUploadingVideo(false)
    }
  }

  const handleThumbnailUpload = async (event) => {
    const file = event.target.files[0]
    if (!file) return

    if (file.size > 5 * 1024 * 1024) {
      alert('Afbeelding is te groot. Maximum 5MB toegestaan.')
      return
    }

    try {
      setUploadingThumbnail(true)
      const response = await uploadAPI.uploadImage(file)
      setPortfolioFormData(prev => ({
        ...prev,
        thumbnailUrl: response.data.file.url
      }))
    } catch (error) {
      console.error('Error uploading thumbnail:', error)
      const msg = error.response?.data?.message || ''
      if (error.code === 'ECONNABORTED') {
        alert('Upload timeout - probeer een kleinere afbeelding.')
      } else {
        alert(msg || 'Fout bij uploaden thumbnail. Probeer het opnieuw.')
      }
    } finally {
      setUploadingThumbnail(false)
    }
  }

  const availableNiches = [
    'beauty', 'fashion', 'fitness', 'health', 'food', 'travel',
    'tech', 'gaming', 'lifestyle', 'parenting', 'pets', 'home',
    'diy', 'automotive', 'sports', 'music', 'art', 'education',
    'business', 'finance', 'sustainability', 'other'
  ]

  const companySizes = [
    '1-10', '11-50', '51-250', '251-1000', '1000+'
  ]

  const agencySizes = ['1-5', '6-20', '21-50', '50+']

  const industryOptions = [
    'E-commerce', 'Fashion & Mode', 'Beauty & Cosmetica', 'Technologie',
    'Gezondheid & Fitness', 'Eten & Drinken', 'Reizen', 'Finance',
    'Vastgoed', 'Entertainment', 'Sport', 'Overig'
  ]

  const handleAddSpecialization = () => {
    const tag = newSpecialization.trim()
    if (tag && !formData.specializations.includes(tag)) {
      setFormData(prev => ({ ...prev, specializations: [...prev.specializations, tag] }))
    }
    setNewSpecialization('')
  }

  const handleRemoveSpecialization = (tag) => {
    setFormData(prev => ({ ...prev, specializations: prev.specializations.filter(s => s !== tag) }))
  }

  const handleCompanyLogoUpload = async (event) => {
    const file = event.target.files[0]
    if (!file) return
    if (file.size > 5 * 1024 * 1024) {
      alert('Bestand is te groot. Maximum 5MB toegestaan.')
      return
    }
    try {
      setUploadingCompanyLogo(true)
      const response = await uploadAPI.uploadImage(file)
      handleInputChange('companyLogo', response.data.file.url)
    } catch (error) {
      console.error('Error uploading company logo:', error)
      alert('Fout bij uploaden logo')
    } finally {
      setUploadingCompanyLogo(false)
    }
  }

  const handleAgencyLogoUpload = async (event) => {
    const file = event.target.files[0]
    if (!file) return
    if (file.size > 5 * 1024 * 1024) {
      alert('Bestand is te groot. Maximum 5MB toegestaan.')
      return
    }
    try {
      setUploadingAgencyLogo(true)
      const response = await uploadAPI.uploadImage(file)
      handleInputChange('agencyLogo', response.data.file.url)
    } catch (error) {
      console.error('Error uploading agency logo:', error)
      alert('Fout bij uploaden logo')
    } finally {
      setUploadingAgencyLogo(false)
    }
  }

  const handleBrandSave = async () => {
    const brands = [...formData.managedBrands]
    if (editingBrandIndex !== null) {
      brands[editingBrandIndex] = brandFormData
    } else {
      brands.push(brandFormData)
    }
    try {
      setLoading(true)
      await usersAPI.updateProfile({ managedBrands: brands })
      await refreshUser()
      setShowBrandForm(false)
      setEditingBrandIndex(null)
      setBrandFormData({ name: '', logo: '', website: '' })
    } catch (error) {
      console.error('Error saving brand:', error)
      alert('Fout bij opslaan merk')
    } finally {
      setLoading(false)
    }
  }

  const handleBrandDelete = async (index) => {
    if (!confirm('Weet je zeker dat je dit merk wilt verwijderen?')) return
    const brands = formData.managedBrands.filter((_, i) => i !== index)
    try {
      setLoading(true)
      await usersAPI.updateProfile({ managedBrands: brands })
      await refreshUser()
    } catch (error) {
      console.error('Error deleting brand:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleTeamSave = async () => {
    const members = [...formData.teamMembers]
    if (editingTeamIndex !== null) {
      members[editingTeamIndex] = teamFormData
    } else {
      members.push(teamFormData)
    }
    try {
      setLoading(true)
      await usersAPI.updateProfile({ teamMembers: members })
      await refreshUser()
      setShowTeamForm(false)
      setEditingTeamIndex(null)
      setTeamFormData({ name: '', email: '', role: 'member' })
    } catch (error) {
      console.error('Error saving team member:', error)
      alert('Fout bij opslaan teamlid')
    } finally {
      setLoading(false)
    }
  }

  const handleTeamDelete = async (index) => {
    if (!confirm('Weet je zeker dat je dit teamlid wilt verwijderen?')) return
    const members = formData.teamMembers.filter((_, i) => i !== index)
    try {
      setLoading(true)
      await usersAPI.updateProfile({ teamMembers: members })
      await refreshUser()
    } catch (error) {
      console.error('Error deleting team member:', error)
    } finally {
      setLoading(false)
    }
  }

  if (!user) {
    navigate('/login')
    return null
  }

  // Check tab completeness for creators
  const getTabCompleteness = (tab) => {
    if (user.role !== 'creator') return true
    switch (tab) {
      case 'profile':
        return !!(user.firstName && user.lastName && user.bio && user.location?.city && (user.age || user.dateOfBirth) && user.niches?.length > 0 && (user.socialMedia?.instagram?.username || user.socialMedia?.tiktok?.username) && user.pricing?.baseRate)
      case 'portfolio':
        return user.portfolio?.length > 0
      case 'billing':
        // Particulier creators don't need KvK, only btwStatus is required
        if (user.btwStatus === 'particulier') return true
        if (user.btwStatus === 'kor') return !!user.kvkNummer
        return !!(user.btwStatus && user.kvkNummer && user.btwNummer)
      case 'payouts':
        return !!(user.stripeAccountActive || user.stripeOnboardingCompleted)
      default:
        return true
    }
  }

  return (
    <div className="font-['Inter'] min-h-screen bg-[#F8FAFC] text-[#0F172A] overflow-x-hidden">
      {/* Animated Background (alleen desktop: zware blur-animaties haperen op telefoons en liggen embedded over het dashboard) */}
      <div className="hidden lg:block fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-1/2 -left-1/2 w-[150%] h-[150%] bg-gradient-to-br from-blue-50 via-transparent to-transparent rounded-full blur-3xl animate-float"></div>
        <div className="absolute -bottom-1/2 -right-1/2 w-[150%] h-[150%] bg-gradient-to-tl from-purple-50 via-transparent to-transparent rounded-full blur-3xl animate-float-delayed"></div>
      </div>

      {/* Header */}
      <header className="w-full flex items-center justify-between px-3 md:px-6 py-3 md:py-4 border-b border-gray-100 sticky top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-xl">
        <Link to="/" className="text-xl md:text-2xl font-black tracking-tighter text-[#0F172A]">
          UGC<span className="text-[#1D8BF8]">4you</span>
        </Link>

        <nav className="hidden md:flex items-center gap-8">
          <Link to="/projects" className="hover:text-[#1D8BF8] transition font-medium text-[#0F172A]">Opdrachten</Link>
          <Link to="/creators" className="hover:text-[#1D8BF8] transition font-medium text-[#0F172A]">Creators</Link>
          <Link to="/dashboard" className="hover:text-[#1D8BF8] transition font-medium text-[#0F172A]">Dashboard</Link>

          <div className="flex items-center gap-4">
            <div className="text-right hidden md:block">
              <div className="text-sm font-semibold text-[#0F172A]">
                {user.role === 'creator' ? user.firstName : user.role === 'agency' ? user.contactPerson : user.contactPerson}
              </div>
              <div className="text-xs text-[#475569]">
                {user.role === 'creator' ? 'Creator' : user.role === 'agency' ? user.agencyName : user.companyName}
              </div>
            </div>
            <div className="w-10 h-10 bg-[#3EB489] rounded-full flex items-center justify-center">
              {user.profilePictureUrl ? (
                <img src={user.profilePictureUrl} alt="Profile" className="w-10 h-10 rounded-full object-cover" />
              ) : (
                <User className="w-5 h-5 text-white" />
              )}
            </div>
          </div>
        </nav>

        <div className="flex items-center gap-1 md:gap-2">
          {/* Mobiele terugweg naar het dashboard: de nav hierboven is verborgen onder md */}
          <Link
            to="/dashboard"
            className="md:hidden inline-flex items-center gap-1.5 px-2.5 py-2 min-h-[44px] rounded-lg text-sm font-medium text-[#0F172A] hover:text-[#1D8BF8] transition"
          >
            <ArrowLeft className="w-4 h-4" />
            Dashboard
          </Link>
          <button
            onClick={handleLogout}
            className="px-2.5 md:px-4 py-2 min-h-[44px] min-w-[44px] justify-center text-sm font-medium text-[#0F172A] hover:text-[#1D8BF8] transition flex items-center gap-1 md:gap-2"
            aria-label="Uitloggen"
          >
            <LogOut className="w-4 h-4" />
            <span className="hidden sm:inline">Uitloggen</span>
          </button>
        </div>
      </header>

      <div className="relative">
        <div className="max-w-6xl mx-auto px-3 md:px-6 py-4 md:py-8">
          {/* Profile Header */}
          <div className="mb-6 md:mb-8 p-5 md:p-8 rounded-2xl md:rounded-3xl bg-white border border-gray-200 shadow-[0_1px_2px_rgba(15,23,42,0.04)]">
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-5 md:gap-6">
              <div className="relative w-20 h-20 md:w-24 md:h-24 bg-[#3EB489] rounded-2xl flex items-center justify-center flex-shrink-0 ring-1 ring-gray-200">
                {user.profilePictureUrl ? (
                  <img
                    src={user.profilePictureUrl}
                    alt="Profile"
                    className="w-full h-full rounded-2xl object-cover"
                  />
                ) : (
                  <span className="text-3xl font-bold text-white">
                    {user.role === 'creator'
                      ? `${user.firstName?.[0] || ''}${user.lastName?.[0] || ''}`
                      : user.role === 'agency'
                        ? user.agencyName?.[0] || 'A'
                        : user.companyName?.[0] || 'U'
                    }
                  </span>
                )}

                {/* Profile Picture Upload Button */}
                <label className="absolute -bottom-2 -right-2 w-8 h-8 bg-white border border-gray-200 rounded-full flex items-center justify-center cursor-pointer hover:border-[#3EB489] transition shadow-sm">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleProfilePictureUpload}
                    className="hidden"
                    disabled={uploadingProfilePic}
                  />
                  {uploadingProfilePic ? (
                    <div className="flex flex-col items-center gap-0.5">
                      <div className="w-4 h-4 border-2 border-gray-200 border-t-[#3EB489] rounded-full animate-spin" />
                      {uploadProgress > 0 && <span className="text-[10px] text-[#475569] font-bold">{uploadProgress}%</span>}
                    </div>
                  ) : (
                    <Camera className="w-4 h-4 text-[#475569]" />
                  )}
                </label>
              </div>

              <div className="flex-1 min-w-0">
                <h1 className="text-2xl md:text-3xl font-black tracking-tight text-[#0F172A] break-words">
                  {user.role === 'creator'
                    ? user.displayName || `${user.firstName} ${user.lastName}`
                    : user.role === 'agency'
                      ? user.agencyName
                      : user.companyName
                  }
                </h1>

                <div className="flex flex-wrap items-center gap-2 mt-4">
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg border border-gray-200 text-[11px] font-medium text-[#475569]">
                    {user.role === 'creator' ? 'Content creator' : user.role === 'agency' ? 'Agency' : 'Bedrijf'}
                  </span>
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg border border-gray-200 text-[11px] text-[#475569]">
                    <MapPin className="w-3 h-3 text-[#94A3B8]" />
                    {user.location?.city || 'Nederland'}
                  </span>
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg border border-gray-200 text-[11px] text-[#475569]">
                    Lid sinds <strong className="font-semibold text-[#0F172A] capitalize">
                      {new Date(user.createdAt).toLocaleDateString('nl-NL', { month: 'long', year: 'numeric' })}
                    </strong>
                  </span>
                  {user.role === 'creator' && user.stats?.averageRating > 0 && (
                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg border border-gray-200 text-[11px] text-[#475569]">
                      <Star className="w-3 h-3 text-[#F59E0B] fill-current" />
                      <strong className="font-semibold text-[#0F172A]">{user.stats.averageRating.toFixed(1)}</strong>
                    </span>
                  )}
                </div>
              </div>

              <button
                data-tour="profile-edit"
                onClick={() => setIsEditing(!isEditing)}
                className={`px-6 py-3.5 rounded-xl font-semibold text-sm transition inline-flex items-center gap-2 flex-shrink-0 ${
                  isEditing
                    ? 'border border-gray-200 text-[#475569] hover:border-[#1D8BF8] hover:text-[#1D8BF8]'
                    : 'bg-[#3EB489] text-white hover:bg-[#359E78] shadow-sm shadow-[#3EB489]/20'
                }`}
              >
                {isEditing ? <X className="w-4 h-4" /> : <Edit className="w-4 h-4" />}
                {isEditing ? 'Annuleren' : 'Bewerken'}
              </button>
            </div>
          </div>

          {/* Success/Error Messages */}
          {saveSuccess && (
            <div className="mb-4 p-4 bg-white border border-[#3EB489]/40 rounded-xl flex items-center gap-3 animate-in fade-in">
              <CheckCircle className="w-5 h-5 text-[#3EB489] flex-shrink-0" />
              <p className="text-sm font-medium text-[#3EB489]">Wijzigingen succesvol opgeslagen!</p>
            </div>
          )}
          {saveError && (
            <div className="mb-4 p-4 bg-white border border-red-200 rounded-xl flex items-center gap-3">
              <X className="w-5 h-5 text-[#EF4444] flex-shrink-0" />
              <p className="text-sm font-medium text-[#EF4444]">{saveError}</p>
            </div>
          )}

          {/* Tabs */}
          {/* Mobiel wrappen (alle tabs zichtbaar, geen verborgen scroll); vanaf md één rij zoals voorheen.
              pt-1.5/pr-2 geven de oranje onvolledig-stip ruimte zodat overflow-x-auto hem niet afknipt. */}
          <div className="flex flex-wrap gap-x-5 gap-y-0 md:flex-nowrap md:gap-7 mb-7 md:mb-8 md:overflow-x-auto pt-1.5 pr-2 border-b border-gray-200">
            {(user.role === 'creator' ? ['profile', 'portfolio', 'moodboard', 'packages', 'billing', 'payouts', 'stats'] : user.role === 'agency' ? ['profile', 'brands', 'team', 'billing'] : ['profile', 'billing', 'stats']).map((tab) => {
              const isComplete = getTabCompleteness(tab)
              return (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`relative pt-2 md:pt-0 pb-2.5 md:pb-3 border-b-2 font-semibold text-sm transition whitespace-nowrap flex items-center gap-2 ${
                  activeTab === tab
                    ? 'border-[#1D8BF8] text-[#1D8BF8]'
                    : !isComplete && user.role === 'creator'
                    ? 'border-transparent text-[#D97706] hover:text-[#B45309]'
                    : 'border-transparent text-[#475569] hover:text-[#0F172A]'
                }`}
              >
                {!isComplete && user.role === 'creator' && activeTab !== tab && (
                  <span className="absolute -top-1 -right-2 w-2 h-2 bg-[#F59E0B] rounded-full"></span>
                )}
                {tab === 'profile' && (
                  <>
                    <User className="w-4 h-4" />
                    Profiel
                  </>
                )}
                {tab === 'portfolio' && (
                  <>
                    <Folder className="w-4 h-4" />
                    Portfolio
                  </>
                )}
                {tab === 'moodboard' && (
                  <>
                    <Image className="w-4 h-4" />
                    Moodboard
                  </>
                )}
                {tab === 'packages' && (
                  <>
                    <Package className="w-4 h-4" />
                    Pakketten
                  </>
                )}
                {tab === 'billing' && (
                  <>
                    <Receipt className="w-4 h-4" />
                    Facturatie
                  </>
                )}
                {tab === 'payouts' && (
                  <>
                    <CreditCard className="w-4 h-4" />
                    Uitbetalingen
                  </>
                )}
                {tab === 'brands' && (
                  <>
                    <Building className="w-4 h-4" />
                    Merken
                  </>
                )}
                {tab === 'team' && (
                  <>
                    <Users className="w-4 h-4" />
                    Team
                  </>
                )}
                {tab === 'stats' && (
                  <>
                    <BarChart3 className="w-4 h-4" />
                    Statistieken
                  </>
                )}
              </button>
            )})}
          </div>

          {/* Content */}
          <div className="relative">
            <div className="relative p-5 md:p-8 rounded-2xl md:rounded-3xl bg-white border border-gray-200 shadow-[0_1px_2px_rgba(15,23,42,0.04)]">

              {/* Profile Tab */}
              {activeTab === 'profile' && (
                <div className="space-y-8 md:space-y-12">
                  {user.role === 'creator' && (
                    <>
                      <ProfileTour
                        ref={profileTourRef}
                        hasCompletedProfile={!!(user.firstName && user.bio && user.niches?.length > 0)}
                      />
                      <div className="flex justify-between items-start gap-3">
                        <div className="min-w-0">
                          <h3 className="text-2xl font-bold mb-1 text-[#0F172A]">Profiel</h3>
                          <p className="text-[#475569] text-sm">Vul je profiel zo volledig mogelijk in voor meer kansen</p>
                        </div>
                        <button
                          onClick={() => profileTourRef.current?.restartTour()}
                          className="p-2.5 flex-shrink-0 rounded-xl border border-gray-200 bg-white hover:bg-sky-50 hover:border-sky-300 text-[#94a3b8] hover:text-[#0ea5e9] transition-all"
                          title="Hulp: tour opnieuw starten"
                          aria-label="Hulp: tour opnieuw starten"
                        >
                          <HelpCircle className="w-5 h-5" />
                        </button>
                      </div>
                    </>
                  )}
                  {user.role === 'creator' ? (
                    <>
                      {/* Basic Info */}
                      <div data-tour="profile-basic">
                        <h3 className="text-lg font-bold tracking-tight mb-5 md:mb-6 text-[#0F172A]">Basis Informatie</h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                          <div>
                            <label className="block text-[11px] font-semibold uppercase tracking-wider text-[#94A3B8] mb-2.5">Voornaam</label>
                            {isEditing ? (
                              <input
                                type="text"
                                value={formData.firstName}
                                onChange={(e) => handleInputChange('firstName', e.target.value)}
                                className="w-full bg-white border border-gray-200 rounded-xl px-4 py-3 focus:ring-2 focus:ring-[#1D8BF8]/50 focus:border-[#1D8BF8] text-[#0F172A]"
                              />
                            ) : (
                              <div className="px-4 py-3 bg-white border border-gray-200 rounded-xl text-[#0F172A]">
                                {user.firstName || <span className="text-[#94A3B8]">Niet ingevuld</span>}
                              </div>
                            )}
                          </div>

                          <div>
                            <label className="block text-[11px] font-semibold uppercase tracking-wider text-[#94A3B8] mb-2.5">Achternaam</label>
                            {isEditing ? (
                              <input
                                type="text"
                                value={formData.lastName}
                                onChange={(e) => handleInputChange('lastName', e.target.value)}
                                className="w-full bg-white border border-gray-200 rounded-xl px-4 py-3 focus:ring-2 focus:ring-[#1D8BF8]/50 focus:border-[#1D8BF8] text-[#0F172A]"
                              />
                            ) : (
                              <div className="px-4 py-3 bg-white border border-gray-200 rounded-xl text-[#0F172A]">
                                {user.lastName || <span className="text-[#94A3B8]">Niet ingevuld</span>}
                              </div>
                            )}
                          </div>

                          <div>
                            <label className="block text-[11px] font-semibold uppercase tracking-wider text-[#94A3B8] mb-2.5">Creator Naam</label>
                            {isEditing ? (
                              <input
                                type="text"
                                value={formData.displayName}
                                onChange={(e) => handleInputChange('displayName', e.target.value)}
                                className="w-full bg-white border border-gray-200 rounded-xl px-4 py-3 focus:ring-2 focus:ring-[#1D8BF8]/50 focus:border-[#1D8BF8] text-[#0F172A]"
                              />
                            ) : (
                              <div className="px-4 py-3 bg-white border border-gray-200 rounded-xl text-[#0F172A]">
                                {user.displayName || <span className="text-[#94A3B8]">Niet ingevuld</span>}
                              </div>
                            )}
                          </div>

                          <div>
                            <label className="block text-[11px] font-semibold uppercase tracking-wider text-[#94A3B8] mb-2.5">Stad</label>
                            {isEditing ? (
                              <input
                                type="text"
                                value={formData.location.city}
                                onChange={(e) => handleInputChange('location.city', e.target.value)}
                                className="w-full bg-white border border-gray-200 rounded-xl px-4 py-3 focus:ring-2 focus:ring-[#1D8BF8]/50 focus:border-[#1D8BF8] text-[#0F172A]"
                              />
                            ) : (
                              <div className="px-4 py-3 bg-white border border-gray-200 rounded-xl text-[#0F172A]">
                                {user.location?.city || <span className="text-[#94A3B8]">Niet ingevuld</span>}
                              </div>
                            )}
                          </div>

                          <div>
                            <label className="block text-[11px] font-semibold uppercase tracking-wider text-[#94A3B8] mb-2.5">Geboortedatum</label>
                            {isEditing ? (
                              <input
                                type="date"
                                value={formData.dateOfBirth}
                                onChange={(e) => handleInputChange('dateOfBirth', e.target.value)}
                                className="w-full bg-white border border-gray-200 rounded-xl px-4 py-3 focus:ring-2 focus:ring-[#1D8BF8]/50 focus:border-[#1D8BF8] text-[#0F172A]"
                              />
                            ) : (
                              <div className="px-4 py-3 bg-white border border-gray-200 rounded-xl text-[#0F172A]">
                                {user.dateOfBirth ? new Date(user.dateOfBirth).toLocaleDateString('nl-NL') : <span className="text-[#94A3B8]">Niet ingevuld</span>}
                              </div>
                            )}
                          </div>
                        </div>

                        <div className="mt-6" data-tour="profile-bio">
                          <label className="block text-[11px] font-semibold uppercase tracking-wider text-[#94A3B8] mb-2.5">Bio</label>
                          {isEditing ? (
                            <>
                              <textarea
                                value={formData.bio}
                                onChange={(e) => handleInputChange('bio', e.target.value)}
                                rows={4}
                                maxLength={1000}
                                className="w-full bg-white border border-gray-200 rounded-xl px-4 py-3 focus:ring-2 focus:ring-[#1D8BF8]/50 focus:border-[#1D8BF8] text-[#0F172A] placeholder-[#475569]/50"
                                placeholder="Vertel over jezelf en je content stijl..."
                              />
                              <div className="mt-1.5 flex justify-end">
                                <span className={`text-xs ${(formData.bio?.length || 0) >= 1000 ? 'text-red-500 font-semibold' : 'text-[#475569]'}`}>
                                  {formData.bio?.length || 0} / 1000 tekens
                                </span>
                              </div>
                            </>
                          ) : (
                            <div className="px-4 py-3 bg-white border border-gray-200 rounded-xl text-[#0F172A] min-h-[100px]">
                              {user.bio || 'Geen bio toegevoegd'}
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Niches */}
                      <div data-tour="profile-niches">
                        <h3 className="text-lg font-bold tracking-tight mb-5 md:mb-6 text-[#0F172A]">Specialisaties</h3>
                        {isEditing ? (
                          <div className="flex flex-wrap gap-2 md:grid md:grid-cols-4 md:gap-3">
                            {availableNiches.map((niche) => (
                              <button
                                key={niche}
                                onClick={() => handleNicheToggle(niche)}
                                className={`px-4 py-2 rounded-xl text-sm font-medium transition ${
                                  formData.niches.includes(niche)
                                    ? 'bg-[#3EB489] text-white'
                                    : 'bg-[#F8FAFC] text-[#475569] hover:bg-gray-100 hover:text-[#0F172A]'
                                }`}
                              >
                                {niche}
                              </button>
                            ))}
                          </div>
                        ) : (
                          <div className="flex flex-wrap gap-2">
                            {user.niches?.length > 0 ? (
                              user.niches.map((niche, index) => (
                                <span
                                  key={index}
                                  className="px-3 py-2 rounded-xl text-sm font-medium border border-gray-200 text-[#475569]"
                                >
                                  {niche}
                                </span>
                              ))
                            ) : (
                              <div className="text-[#94A3B8] text-sm">Geen specialisaties toegevoegd</div>
                            )}
                          </div>
                        )}
                      </div>

                      {/* Social Media */}
                      <div data-tour="profile-social">
                        <div className="flex justify-between items-center mb-6">
                          <h3 className="text-xl font-bold text-[#0F172A]">Social Media</h3>
                        </div>

                        <div className="space-y-6">
                          {/* Instagram */}
                          <div className="bg-gradient-to-r from-pink-50 to-purple-50 border border-pink-200 rounded-2xl p-4 md:p-6">
                            <div className="flex items-center gap-3 mb-4">
                              <div className="w-8 h-8 bg-gradient-to-r from-pink-500 to-purple-600 rounded-lg flex items-center justify-center">
                                <Instagram className="w-5 h-5 text-white" />
                              </div>
                              <h4 className="font-semibold text-[#0F172A]">Instagram</h4>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              <div>
                                <label className="block text-[11px] font-semibold uppercase tracking-wider text-[#94A3B8] mb-2.5">Username</label>
                                {isEditing ? (
                                  <input
                                    type="text"
                                    value={formData.socialMedia.instagram?.username || ''}
                                    onChange={(e) => handleInputChange('socialMedia.instagram.username', e.target.value)}
                                    className="w-full bg-white border border-pink-200 rounded-xl px-4 py-3 focus:ring-2 focus:ring-pink-400/50 focus:border-pink-400 text-[#0F172A]"
                                    placeholder="@jouw_instagram"
                                  />
                                ) : (
                                  <div className="px-4 py-3 bg-white rounded-xl text-[#0F172A] flex items-center gap-2">
                                    {user.socialMedia?.instagram?.username ? (
                                      <>
                                        @{user.socialMedia.instagram.username}
                                        <a
                                          href={`https://instagram.com/${user.socialMedia.instagram.username}`}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          className="text-pink-400 hover:text-pink-300 transition"
                                        >
                                          <ExternalLink className="w-4 h-4" />
                                        </a>
                                      </>
                                    ) : (
                                      'Niet gekoppeld'
                                    )}
                                  </div>
                                )}
                              </div>

                              <div>
                                <label className="block text-[11px] font-semibold uppercase tracking-wider text-[#94A3B8] mb-2.5">Followers</label>
                                {isEditing ? (
                                  <input
                                    type="number"
                                    value={formData.socialMedia.instagram?.followers || ''}
                                    onChange={(e) => handleInputChange('socialMedia.instagram.followers', parseInt(e.target.value) || 0)}
                                    className="w-full bg-white border border-pink-200 rounded-xl px-4 py-3 focus:ring-2 focus:ring-pink-400/50 focus:border-pink-400 text-[#0F172A]"
                                  />
                                ) : (
                                  <div className="px-4 py-3 bg-white rounded-xl text-[#0F172A]">
                                    {user.socialMedia?.instagram?.followers?.toLocaleString() || '0'}
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>

                          {/* TikTok */}
                          <div className="bg-[#F8FAFC] border border-gray-200 rounded-2xl p-4 md:p-6">
                            <div className="flex items-center gap-3 mb-4">
                              <div className="w-8 h-8 bg-black rounded-lg flex items-center justify-center">
                                <span className="text-white font-bold text-sm">TT</span>
                              </div>
                              <h4 className="font-semibold text-[#0F172A]">TikTok</h4>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              <div>
                                <label className="block text-[11px] font-semibold uppercase tracking-wider text-[#94A3B8] mb-2.5">Username</label>
                                {isEditing ? (
                                  <input
                                    type="text"
                                    value={formData.socialMedia.tiktok?.username || ''}
                                    onChange={(e) => handleInputChange('socialMedia.tiktok.username', e.target.value)}
                                    className="w-full bg-white border border-gray-200 rounded-xl px-4 py-3 focus:ring-2 focus:ring-red-400/50 focus:border-red-400 text-[#0F172A]"
                                    placeholder="@jouw_tiktok"
                                  />
                                ) : (
                                  <div className="px-4 py-3 bg-white rounded-xl text-[#0F172A] flex items-center gap-2">
                                    {user.socialMedia?.tiktok?.username ? (
                                      <>
                                        @{user.socialMedia.tiktok.username}
                                        <a
                                          href={`https://tiktok.com/@${user.socialMedia.tiktok.username}`}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          className="text-red-400 hover:text-red-300 transition"
                                        >
                                          <ExternalLink className="w-4 h-4" />
                                        </a>
                                      </>
                                    ) : (
                                      'Niet gekoppeld'
                                    )}
                                  </div>
                                )}
                              </div>

                              <div>
                                <label className="block text-[11px] font-semibold uppercase tracking-wider text-[#94A3B8] mb-2.5">Followers</label>
                                {isEditing ? (
                                  <input
                                    type="number"
                                    value={formData.socialMedia.tiktok?.followers || ''}
                                    onChange={(e) => handleInputChange('socialMedia.tiktok.followers', parseInt(e.target.value) || 0)}
                                    className="w-full bg-white border border-gray-200 rounded-xl px-4 py-3 focus:ring-2 focus:ring-red-400/50 focus:border-red-400 text-[#0F172A]"
                                  />
                                ) : (
                                  <div className="px-4 py-3 bg-white rounded-xl text-[#0F172A]">
                                    {user.socialMedia?.tiktok?.followers?.toLocaleString() || '0'}
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>

                          {/* YouTube */}
                          <div className="bg-gradient-to-r from-red-50 to-red-50 border border-red-200 rounded-2xl p-4 md:p-6">
                            <div className="flex items-center gap-3 mb-4">
                              <div className="w-8 h-8 bg-red-600 rounded-lg flex items-center justify-center">
                                <Youtube className="w-5 h-5 text-white" />
                              </div>
                              <h4 className="font-semibold text-[#0F172A]">YouTube</h4>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              <div>
                                <label className="block text-[11px] font-semibold uppercase tracking-wider text-[#94A3B8] mb-2.5">Channel Name</label>
                                {isEditing ? (
                                  <input
                                    type="text"
                                    value={formData.socialMedia.youtube?.username || ''}
                                    onChange={(e) => handleInputChange('socialMedia.youtube.username', e.target.value)}
                                    className="w-full bg-white border border-red-200 rounded-xl px-4 py-3 focus:ring-2 focus:ring-red-400/50 focus:border-red-400 text-[#0F172A]"
                                    placeholder="Jouw YouTube kanaal"
                                  />
                                ) : (
                                  <div className="px-4 py-3 bg-white rounded-xl text-[#0F172A] flex items-center gap-2">
                                    {user.socialMedia?.youtube?.username ? (
                                      <>
                                        {user.socialMedia.youtube.username}
                                        <ExternalLink className="w-4 h-4 text-red-400" />
                                      </>
                                    ) : (
                                      'Niet gekoppeld'
                                    )}
                                  </div>
                                )}
                              </div>

                              <div>
                                <label className="block text-[11px] font-semibold uppercase tracking-wider text-[#94A3B8] mb-2.5">Subscribers</label>
                                {isEditing ? (
                                  <input
                                    type="number"
                                    value={formData.socialMedia.youtube?.subscribers || ''}
                                    onChange={(e) => handleInputChange('socialMedia.youtube.subscribers', parseInt(e.target.value) || 0)}
                                    className="w-full bg-white border border-red-200 rounded-xl px-4 py-3 focus:ring-2 focus:ring-red-400/50 focus:border-red-400 text-[#0F172A]"
                                  />
                                ) : (
                                  <div className="px-4 py-3 bg-white rounded-xl text-[#0F172A]">
                                    {user.socialMedia?.youtube?.subscribers?.toLocaleString() || '0'}
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Pricing */}
                      <div data-tour="profile-pricing">
                        <h3 className="text-lg font-bold tracking-tight mb-5 md:mb-6 text-[#0F172A]">Prijzen</h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                          <div>
                            <label className="block text-[11px] font-semibold uppercase tracking-wider text-[#94A3B8] mb-2.5">Basis Tarief (€)</label>
                            {isEditing ? (
                              <>
                                <input
                                  type="number"
                                  min="25"
                                  value={formData.pricing.baseRate}
                                  onChange={(e) => handleInputChange('pricing.baseRate', parseInt(e.target.value) || 0)}
                                  className="w-full bg-white border border-gray-200 rounded-xl px-4 py-3 focus:ring-2 focus:ring-[#1D8BF8]/50 focus:border-[#1D8BF8] text-[#0F172A]"
                                />
                                <p className="text-xs text-[#94A3B8] mt-1.5">Minimaal €25 per video</p>
                              </>
                            ) : (
                              <div className="px-4 py-3 bg-white border border-gray-200 rounded-xl text-[#0F172A]">
                                €{user.pricing?.baseRate || <span className="text-[#94A3B8]">Niet ingevuld</span>}
                              </div>
                            )}
                          </div>

                          <div className="flex items-center">
                            {isEditing ? (
                              <label className="flex items-center py-2 cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={formData.pricing.negotiable}
                                  onChange={(e) => handleInputChange('pricing.negotiable', e.target.checked)}
                                  className="mr-3 w-5 h-5 accent-[#3EB489]"
                                />
                                <span className="text-[#0F172A]">Onderhandelbaar</span>
                              </label>
                            ) : (
                              <div className="px-4 py-3 bg-white border border-gray-200 rounded-xl text-[#0F172A]">
                                {user.pricing?.negotiable ? 'Onderhandelbaar' : 'Vast tarief'}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </>
                  ) : user.role === 'agency' ? (
                    <>
                      {/* Agency Info */}
                      <div>
                        <h3 className="text-lg font-bold tracking-tight mb-5 md:mb-6 text-[#0F172A]">Agency Informatie</h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                          <div>
                            <label className="block text-[11px] font-semibold uppercase tracking-wider text-[#94A3B8] mb-2.5">Agency Naam</label>
                            {isEditing ? (
                              <input
                                type="text"
                                value={formData.agencyName}
                                onChange={(e) => handleInputChange('agencyName', e.target.value)}
                                className="w-full bg-white border border-gray-200 rounded-xl px-4 py-3 focus:ring-2 focus:ring-[#1D8BF8]/50 focus:border-[#1D8BF8] text-[#0F172A]"
                              />
                            ) : (
                              <div className="px-4 py-3 bg-white border border-gray-200 rounded-xl text-[#0F172A]">
                                {user.agencyName || <span className="text-[#94A3B8]">Niet ingevuld</span>}
                              </div>
                            )}
                          </div>

                          <div>
                            <label className="block text-[11px] font-semibold uppercase tracking-wider text-[#94A3B8] mb-2.5">Contactpersoon</label>
                            {isEditing ? (
                              <input
                                type="text"
                                value={formData.contactPerson}
                                onChange={(e) => handleInputChange('contactPerson', e.target.value)}
                                className="w-full bg-white border border-gray-200 rounded-xl px-4 py-3 focus:ring-2 focus:ring-[#1D8BF8]/50 focus:border-[#1D8BF8] text-[#0F172A]"
                              />
                            ) : (
                              <div className="px-4 py-3 bg-white border border-gray-200 rounded-xl text-[#0F172A]">
                                {user.contactPerson || <span className="text-[#94A3B8]">Niet ingevuld</span>}
                              </div>
                            )}
                          </div>

                          <div>
                            <label className="block text-[11px] font-semibold uppercase tracking-wider text-[#94A3B8] mb-2.5">Agency Grootte</label>
                            {isEditing ? (
                              <select
                                value={formData.agencySize}
                                onChange={(e) => handleInputChange('agencySize', e.target.value)}
                                className="w-full bg-white border border-gray-200 rounded-xl px-4 py-3 focus:ring-2 focus:ring-[#1D8BF8]/50 focus:border-[#1D8BF8] text-[#0F172A]"
                              >
                                <option value="">Selecteer grootte</option>
                                {agencySizes.map(size => (
                                  <option key={size} value={size}>{size} medewerkers</option>
                                ))}
                              </select>
                            ) : (
                              <div className="px-4 py-3 bg-white border border-gray-200 rounded-xl text-[#0F172A]">
                                {user.agencySize ? `${user.agencySize} medewerkers` : <span className="text-[#94A3B8]">Niet ingevuld</span>}
                              </div>
                            )}
                          </div>

                          <div>
                            <label className="block text-[11px] font-semibold uppercase tracking-wider text-[#94A3B8] mb-2.5">Locatie</label>
                            {isEditing ? (
                              <input
                                type="text"
                                value={formData.location.city}
                                onChange={(e) => handleInputChange('location.city', e.target.value)}
                                className="w-full bg-white border border-gray-200 rounded-xl px-4 py-3 focus:ring-2 focus:ring-[#1D8BF8]/50 focus:border-[#1D8BF8] text-[#0F172A]"
                                placeholder="Stad"
                              />
                            ) : (
                              <div className="px-4 py-3 bg-white border border-gray-200 rounded-xl text-[#0F172A]">
                                {user.location?.city || <span className="text-[#94A3B8]">Niet ingevuld</span>}
                              </div>
                            )}
                          </div>

                          <div>
                            <label className="block text-[11px] font-semibold uppercase tracking-wider text-[#94A3B8] mb-2.5">Branche</label>
                            {isEditing ? (
                              <select
                                value={formData.industry}
                                onChange={(e) => handleInputChange('industry', e.target.value)}
                                className="w-full bg-white border border-gray-200 rounded-xl px-4 py-3 focus:ring-2 focus:ring-[#1D8BF8]/50 focus:border-[#1D8BF8] text-[#0F172A]"
                              >
                                <option value="">Selecteer branche</option>
                                {industryOptions.map(ind => (
                                  <option key={ind} value={ind}>{ind}</option>
                                ))}
                              </select>
                            ) : (
                              <div className="px-4 py-3 bg-white border border-gray-200 rounded-xl text-[#0F172A]">
                                {user.industry || <span className="text-[#94A3B8]">Niet ingevuld</span>}
                              </div>
                            )}
                          </div>

                          <div>
                            <label className="block text-[11px] font-semibold uppercase tracking-wider text-[#94A3B8] mb-2.5">Website</label>
                            {isEditing ? (
                              <input
                                type="url"
                                value={formData.website}
                                onChange={(e) => handleInputChange('website', e.target.value)}
                                className="w-full bg-white border border-gray-200 rounded-xl px-4 py-3 focus:ring-2 focus:ring-[#1D8BF8]/50 focus:border-[#1D8BF8] text-[#0F172A]"
                                placeholder="https://jouwagency.nl"
                              />
                            ) : (
                              <div className="px-4 py-3 bg-white border border-gray-200 rounded-xl text-[#0F172A]">
                                {user.website ? (
                                  <a href={user.website} target="_blank" rel="noopener noreferrer" className="text-[#1D8BF8] hover:text-[#1670c9]">
                                    {user.website}
                                  </a>
                                ) : (
                                  <span className="text-[#94A3B8]">Niet ingevuld</span>
                                )}
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Agency Bio */}
                        <div className="mt-6">
                          <label className="block text-[11px] font-semibold uppercase tracking-wider text-[#94A3B8] mb-2.5">Over de agency</label>
                          {isEditing ? (
                            <textarea
                              value={formData.agencyBio}
                              onChange={(e) => handleInputChange('agencyBio', e.target.value)}
                              rows={4}
                              maxLength={1500}
                              className="w-full bg-white border border-gray-200 rounded-xl px-4 py-3 focus:ring-2 focus:ring-[#1D8BF8]/50 focus:border-[#1D8BF8] text-[#0F172A] placeholder-[#475569]/50"
                              placeholder="Vertel over je agency..."
                            />
                          ) : (
                            <div className="px-4 py-3 bg-white border border-gray-200 rounded-xl text-[#0F172A] min-h-[100px]">
                              {user.agencyBio || <span className="text-[#94A3B8]">Geen beschrijving toegevoegd</span>}
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Agency Specializations */}
                      <div>
                        <h3 className="text-lg font-bold tracking-tight mb-5 md:mb-6 text-[#0F172A]">Specialisaties</h3>
                        {isEditing ? (
                          <div>
                            <div className="flex gap-2 mb-4">
                              <input
                                type="text"
                                value={newSpecialization}
                                onChange={(e) => setNewSpecialization(e.target.value)}
                                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAddSpecialization() } }}
                                className="flex-1 bg-white border border-gray-200 rounded-xl px-4 py-3 focus:ring-2 focus:ring-[#1D8BF8]/50 focus:border-[#1D8BF8] text-[#0F172A]"
                                placeholder="Typ een specialisatie en druk Enter..."
                              />
                              <button
                                type="button"
                                onClick={handleAddSpecialization}
                                className="px-4 py-3 bg-[#3EB489] text-white rounded-xl hover:bg-[#359E78] transition"
                              >
                                <Plus className="w-4 h-4" />
                              </button>
                            </div>
                            <div className="flex flex-wrap gap-2">
                              {formData.specializations.map((tag, index) => (
                                <span key={index} className="inline-flex items-center gap-1 px-3 py-2 rounded-xl text-sm font-medium border border-gray-200 text-[#475569]">
                                  {tag}
                                  <button type="button" onClick={() => handleRemoveSpecialization(tag)} className="ml-1 hover:text-red-500">
                                    <X className="w-3 h-3" />
                                  </button>
                                </span>
                              ))}
                            </div>
                          </div>
                        ) : (
                          <div className="flex flex-wrap gap-2">
                            {user.specializations?.length > 0 ? (
                              user.specializations.map((tag, index) => (
                                <span key={index} className="px-3 py-2 rounded-xl text-sm font-medium border border-gray-200 text-[#475569]">
                                  {tag}
                                </span>
                              ))
                            ) : (
                              <div className="text-[#94A3B8] text-sm">Geen specialisaties toegevoegd</div>
                            )}
                          </div>
                        )}
                      </div>

                      {/* Agency Social Media */}
                      <div>
                        <h3 className="text-lg font-bold tracking-tight mb-5 md:mb-6 text-[#0F172A]">Social Media</h3>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                          <div>
                            <label className="block text-[11px] font-semibold uppercase tracking-wider text-[#94A3B8] mb-2.5">Instagram URL</label>
                            {isEditing ? (
                              <input
                                type="text"
                                value={formData.socialMedia.instagram?.username || ''}
                                onChange={(e) => handleInputChange('socialMedia.instagram.username', e.target.value)}
                                className="w-full bg-white border border-gray-200 rounded-xl px-4 py-3 focus:ring-2 focus:ring-[#1D8BF8]/50 focus:border-[#1D8BF8] text-[#0F172A]"
                                placeholder="@jouw_instagram"
                              />
                            ) : (
                              <div className="px-4 py-3 bg-white border border-gray-200 rounded-xl text-[#0F172A]">
                                {user.socialMedia?.instagram?.username ? `@${user.socialMedia.instagram.username}` : <span className="text-[#94A3B8]">Niet ingevuld</span>}
                              </div>
                            )}
                          </div>

                          <div>
                            <label className="block text-[11px] font-semibold uppercase tracking-wider text-[#94A3B8] mb-2.5">LinkedIn URL</label>
                            {isEditing ? (
                              <input
                                type="text"
                                value={formData.socialMedia.other?.find(o => o.platform === 'linkedin')?.url || ''}
                                onChange={(e) => {
                                  const others = [...(formData.socialMedia.other || [])]
                                  const idx = others.findIndex(o => o.platform === 'linkedin')
                                  if (idx >= 0) {
                                    others[idx] = { platform: 'linkedin', url: e.target.value }
                                  } else {
                                    others.push({ platform: 'linkedin', url: e.target.value })
                                  }
                                  handleInputChange('socialMedia.other', others)
                                }}
                                className="w-full bg-white border border-gray-200 rounded-xl px-4 py-3 focus:ring-2 focus:ring-[#1D8BF8]/50 focus:border-[#1D8BF8] text-[#0F172A]"
                                placeholder="https://linkedin.com/company/..."
                              />
                            ) : (
                              <div className="px-4 py-3 bg-white border border-gray-200 rounded-xl text-[#0F172A]">
                                {user.socialMedia?.other?.find(o => o.platform === 'linkedin')?.url || <span className="text-[#94A3B8]">Niet ingevuld</span>}
                              </div>
                            )}
                          </div>

                          <div>
                            <label className="block text-[11px] font-semibold uppercase tracking-wider text-[#94A3B8] mb-2.5">Website URL</label>
                            {isEditing ? (
                              <input
                                type="url"
                                value={formData.website}
                                onChange={(e) => handleInputChange('website', e.target.value)}
                                className="w-full bg-white border border-gray-200 rounded-xl px-4 py-3 focus:ring-2 focus:ring-[#1D8BF8]/50 focus:border-[#1D8BF8] text-[#0F172A]"
                                placeholder="https://jouwagency.nl"
                              />
                            ) : (
                              <div className="px-4 py-3 bg-white border border-gray-200 rounded-xl text-[#0F172A]">
                                {user.website ? (
                                  <a href={user.website} target="_blank" rel="noopener noreferrer" className="text-[#1D8BF8] hover:text-[#1670c9]">
                                    {user.website}
                                  </a>
                                ) : (
                                  <span className="text-[#94A3B8]">Niet ingevuld</span>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Agency Logo */}
                      <div>
                        <h3 className="text-lg font-bold tracking-tight mb-5 md:mb-6 text-[#0F172A]">Agency Logo</h3>
                        <div className="flex flex-wrap items-center gap-4 md:gap-6">
                          {formData.agencyLogo ? (
                            <img src={formData.agencyLogo} alt="Agency logo" className="w-24 h-24 rounded-2xl object-cover border border-gray-200" />
                          ) : (
                            <div className="w-24 h-24 rounded-2xl bg-white border border-gray-200 flex items-center justify-center">
                              <Building className="w-10 h-10 text-[#475569]/30" />
                            </div>
                          )}
                          {isEditing && (
                            <label className="px-6 py-3.5 bg-[#3EB489] text-white rounded-xl font-semibold text-sm hover:bg-[#359E78] transition inline-flex items-center cursor-pointer shadow-sm shadow-[#3EB489]/20">
                              <input
                                type="file"
                                accept="image/*"
                                onChange={handleAgencyLogoUpload}
                                className="hidden"
                                disabled={uploadingAgencyLogo}
                              />
                              {uploadingAgencyLogo ? (
                                <>
                                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2" />
                                  Uploaden...
                                </>
                              ) : (
                                <>
                                  <Upload className="w-4 h-4 mr-2" />
                                  Logo Uploaden
                                </>
                              )}
                            </label>
                          )}
                        </div>
                      </div>
                    </>
                  ) : (
                    <>
                      {/* Company Info */}
                      <div>
                        <h3 className="text-lg font-bold tracking-tight mb-5 md:mb-6 text-[#0F172A]">Bedrijf Informatie</h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                          <div>
                            <label className="block text-[11px] font-semibold uppercase tracking-wider text-[#94A3B8] mb-2.5">Bedrijfsnaam</label>
                            {isEditing ? (
                              <input
                                type="text"
                                value={formData.companyName}
                                onChange={(e) => handleInputChange('companyName', e.target.value)}
                                className="w-full bg-white border border-gray-200 rounded-xl px-4 py-3 focus:ring-2 focus:ring-[#1D8BF8]/50 focus:border-[#1D8BF8] text-[#0F172A]"
                              />
                            ) : (
                              <div className="px-4 py-3 bg-white border border-gray-200 rounded-xl text-[#0F172A]">
                                {user.companyName || <span className="text-[#94A3B8]">Niet ingevuld</span>}
                              </div>
                            )}
                          </div>

                          <div>
                            <label className="block text-[11px] font-semibold uppercase tracking-wider text-[#94A3B8] mb-2.5">Contactpersoon</label>
                            {isEditing ? (
                              <input
                                type="text"
                                value={formData.contactPerson}
                                onChange={(e) => handleInputChange('contactPerson', e.target.value)}
                                className="w-full bg-white border border-gray-200 rounded-xl px-4 py-3 focus:ring-2 focus:ring-[#1D8BF8]/50 focus:border-[#1D8BF8] text-[#0F172A]"
                              />
                            ) : (
                              <div className="px-4 py-3 bg-white border border-gray-200 rounded-xl text-[#0F172A]">
                                {user.contactPerson || <span className="text-[#94A3B8]">Niet ingevuld</span>}
                              </div>
                            )}
                          </div>

                          <div>
                            <label className="block text-[11px] font-semibold uppercase tracking-wider text-[#94A3B8] mb-2.5">Bedrijfsgrootte</label>
                            {isEditing ? (
                              <select
                                value={formData.companySize}
                                onChange={(e) => handleInputChange('companySize', e.target.value)}
                                className="w-full bg-white border border-gray-200 rounded-xl px-4 py-3 focus:ring-2 focus:ring-[#1D8BF8]/50 focus:border-[#1D8BF8] text-[#0F172A]"
                              >
                                <option value="">Selecteer grootte</option>
                                {companySizes.map(size => (
                                  <option key={size} value={size}>{size} medewerkers</option>
                                ))}
                              </select>
                            ) : (
                              <div className="px-4 py-3 bg-white border border-gray-200 rounded-xl text-[#0F172A]">
                                {user.companySize ? `${user.companySize} medewerkers` : <span className="text-[#94A3B8]">Niet ingevuld</span>}
                              </div>
                            )}
                          </div>

                          <div>
                            <label className="block text-[11px] font-semibold uppercase tracking-wider text-[#94A3B8] mb-2.5">Website</label>
                            {isEditing ? (
                              <input
                                type="url"
                                value={formData.website}
                                onChange={(e) => handleInputChange('website', e.target.value)}
                                className="w-full bg-white border border-gray-200 rounded-xl px-4 py-3 focus:ring-2 focus:ring-[#1D8BF8]/50 focus:border-[#1D8BF8] text-[#0F172A]"
                                placeholder="https://jouwbedrijf.nl"
                              />
                            ) : (
                              <div className="px-4 py-3 bg-white border border-gray-200 rounded-xl text-[#0F172A]">
                                {user.website ? (
                                  <a href={user.website} target="_blank" rel="noopener noreferrer" className="text-[#1D8BF8] hover:text-[#1670c9]">
                                    {user.website}
                                  </a>
                                ) : (
                                  <span className="text-[#94A3B8]">Niet ingevuld</span>
                                )}
                              </div>
                            )}
                          </div>

                          <div>
                            <label className="block text-[11px] font-semibold uppercase tracking-wider text-[#94A3B8] mb-2.5">Locatie</label>
                            {isEditing ? (
                              <input
                                type="text"
                                value={formData.location.city}
                                onChange={(e) => handleInputChange('location.city', e.target.value)}
                                className="w-full bg-white border border-gray-200 rounded-xl px-4 py-3 focus:ring-2 focus:ring-[#1D8BF8]/50 focus:border-[#1D8BF8] text-[#0F172A]"
                                placeholder="Stad"
                              />
                            ) : (
                              <div className="px-4 py-3 bg-white border border-gray-200 rounded-xl text-[#0F172A]">
                                {user.location?.city || <span className="text-[#94A3B8]">Niet ingevuld</span>}
                              </div>
                            )}
                          </div>

                          <div>
                            <label className="block text-[11px] font-semibold uppercase tracking-wider text-[#94A3B8] mb-2.5">Branche</label>
                            {isEditing ? (
                              <select
                                value={formData.industry}
                                onChange={(e) => handleInputChange('industry', e.target.value)}
                                className="w-full bg-white border border-gray-200 rounded-xl px-4 py-3 focus:ring-2 focus:ring-[#1D8BF8]/50 focus:border-[#1D8BF8] text-[#0F172A]"
                              >
                                <option value="">Selecteer branche</option>
                                {industryOptions.map(ind => (
                                  <option key={ind} value={ind}>{ind}</option>
                                ))}
                              </select>
                            ) : (
                              <div className="px-4 py-3 bg-white border border-gray-200 rounded-xl text-[#0F172A]">
                                {user.industry || <span className="text-[#94A3B8]">Niet ingevuld</span>}
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Company Bio */}
                        <div className="mt-6">
                          <label className="block text-[11px] font-semibold uppercase tracking-wider text-[#94A3B8] mb-2.5">Over het bedrijf</label>
                          {isEditing ? (
                            <textarea
                              value={formData.companyBio}
                              onChange={(e) => handleInputChange('companyBio', e.target.value)}
                              rows={4}
                              maxLength={1000}
                              className="w-full bg-white border border-gray-200 rounded-xl px-4 py-3 focus:ring-2 focus:ring-[#1D8BF8]/50 focus:border-[#1D8BF8] text-[#0F172A] placeholder-[#475569]/50"
                              placeholder="Vertel over je bedrijf..."
                            />
                          ) : (
                            <div className="px-4 py-3 bg-white border border-gray-200 rounded-xl text-[#0F172A] min-h-[100px]">
                              {user.companyBio || <span className="text-[#94A3B8]">Geen beschrijving toegevoegd</span>}
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Company Specializations */}
                      <div>
                        <h3 className="text-lg font-bold tracking-tight mb-5 md:mb-6 text-[#0F172A]">Specialisaties</h3>
                        {isEditing ? (
                          <div>
                            <div className="flex gap-2 mb-4">
                              <input
                                type="text"
                                value={newSpecialization}
                                onChange={(e) => setNewSpecialization(e.target.value)}
                                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAddSpecialization() } }}
                                className="flex-1 bg-white border border-gray-200 rounded-xl px-4 py-3 focus:ring-2 focus:ring-[#1D8BF8]/50 focus:border-[#1D8BF8] text-[#0F172A]"
                                placeholder="Typ een specialisatie en druk Enter..."
                              />
                              <button
                                type="button"
                                onClick={handleAddSpecialization}
                                className="px-4 py-3 bg-[#3EB489] text-white rounded-xl hover:bg-[#359E78] transition"
                              >
                                <Plus className="w-4 h-4" />
                              </button>
                            </div>
                            <div className="flex flex-wrap gap-2">
                              {formData.specializations.map((tag, index) => (
                                <span key={index} className="inline-flex items-center gap-1 px-3 py-2 rounded-xl text-sm font-medium border border-gray-200 text-[#475569]">
                                  {tag}
                                  <button type="button" onClick={() => handleRemoveSpecialization(tag)} className="ml-1 hover:text-red-500">
                                    <X className="w-3 h-3" />
                                  </button>
                                </span>
                              ))}
                            </div>
                          </div>
                        ) : (
                          <div className="flex flex-wrap gap-2">
                            {user.specializations?.length > 0 ? (
                              user.specializations.map((tag, index) => (
                                <span key={index} className="px-3 py-2 rounded-xl text-sm font-medium border border-gray-200 text-[#475569]">
                                  {tag}
                                </span>
                              ))
                            ) : (
                              <div className="text-[#94A3B8] text-sm">Geen specialisaties toegevoegd</div>
                            )}
                          </div>
                        )}
                      </div>

                      {/* Company Social Media */}
                      <div>
                        <h3 className="text-lg font-bold tracking-tight mb-5 md:mb-6 text-[#0F172A]">Social Media</h3>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                          <div>
                            <label className="block text-[11px] font-semibold uppercase tracking-wider text-[#94A3B8] mb-2.5">Instagram URL</label>
                            {isEditing ? (
                              <input
                                type="text"
                                value={formData.socialMedia.instagram?.username || ''}
                                onChange={(e) => handleInputChange('socialMedia.instagram.username', e.target.value)}
                                className="w-full bg-white border border-gray-200 rounded-xl px-4 py-3 focus:ring-2 focus:ring-[#1D8BF8]/50 focus:border-[#1D8BF8] text-[#0F172A]"
                                placeholder="@jouw_instagram"
                              />
                            ) : (
                              <div className="px-4 py-3 bg-white border border-gray-200 rounded-xl text-[#0F172A]">
                                {user.socialMedia?.instagram?.username ? `@${user.socialMedia.instagram.username}` : <span className="text-[#94A3B8]">Niet ingevuld</span>}
                              </div>
                            )}
                          </div>

                          <div>
                            <label className="block text-[11px] font-semibold uppercase tracking-wider text-[#94A3B8] mb-2.5">LinkedIn URL</label>
                            {isEditing ? (
                              <input
                                type="text"
                                value={formData.socialMedia.other?.find(o => o.platform === 'linkedin')?.url || ''}
                                onChange={(e) => {
                                  const others = [...(formData.socialMedia.other || [])]
                                  const idx = others.findIndex(o => o.platform === 'linkedin')
                                  if (idx >= 0) {
                                    others[idx] = { platform: 'linkedin', url: e.target.value }
                                  } else {
                                    others.push({ platform: 'linkedin', url: e.target.value })
                                  }
                                  handleInputChange('socialMedia.other', others)
                                }}
                                className="w-full bg-white border border-gray-200 rounded-xl px-4 py-3 focus:ring-2 focus:ring-[#1D8BF8]/50 focus:border-[#1D8BF8] text-[#0F172A]"
                                placeholder="https://linkedin.com/company/..."
                              />
                            ) : (
                              <div className="px-4 py-3 bg-white border border-gray-200 rounded-xl text-[#0F172A]">
                                {user.socialMedia?.other?.find(o => o.platform === 'linkedin')?.url || <span className="text-[#94A3B8]">Niet ingevuld</span>}
                              </div>
                            )}
                          </div>

                          <div>
                            <label className="block text-[11px] font-semibold uppercase tracking-wider text-[#94A3B8] mb-2.5">Website URL</label>
                            {isEditing ? (
                              <input
                                type="url"
                                value={formData.website}
                                onChange={(e) => handleInputChange('website', e.target.value)}
                                className="w-full bg-white border border-gray-200 rounded-xl px-4 py-3 focus:ring-2 focus:ring-[#1D8BF8]/50 focus:border-[#1D8BF8] text-[#0F172A]"
                                placeholder="https://jouwbedrijf.nl"
                              />
                            ) : (
                              <div className="px-4 py-3 bg-white border border-gray-200 rounded-xl text-[#0F172A]">
                                {user.website ? (
                                  <a href={user.website} target="_blank" rel="noopener noreferrer" className="text-[#1D8BF8] hover:text-[#1670c9]">
                                    {user.website}
                                  </a>
                                ) : (
                                  <span className="text-[#94A3B8]">Niet ingevuld</span>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Company Logo */}
                      <div>
                        <h3 className="text-lg font-bold tracking-tight mb-5 md:mb-6 text-[#0F172A]">Bedrijfslogo</h3>
                        <div className="flex flex-wrap items-center gap-4 md:gap-6">
                          {formData.companyLogo ? (
                            <img src={formData.companyLogo} alt="Company logo" className="w-24 h-24 rounded-2xl object-cover border border-gray-200" />
                          ) : (
                            <div className="w-24 h-24 rounded-2xl bg-white border border-gray-200 flex items-center justify-center">
                              <Building className="w-10 h-10 text-[#475569]/30" />
                            </div>
                          )}
                          {isEditing && (
                            <label className="px-6 py-3.5 bg-[#3EB489] text-white rounded-xl font-semibold text-sm hover:bg-[#359E78] transition inline-flex items-center cursor-pointer shadow-sm shadow-[#3EB489]/20">
                              <input
                                type="file"
                                accept="image/*"
                                onChange={handleCompanyLogoUpload}
                                className="hidden"
                                disabled={uploadingCompanyLogo}
                              />
                              {uploadingCompanyLogo ? (
                                <>
                                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2" />
                                  Uploaden...
                                </>
                              ) : (
                                <>
                                  <Upload className="w-4 h-4 mr-2" />
                                  Logo Uploaden
                                </>
                              )}
                            </label>
                          )}
                        </div>
                      </div>
                    </>
                  )}

                  {/* Save Button */}
                  {isEditing && (
                    <div className="flex justify-end gap-4">
                      <button
                        onClick={() => setIsEditing(false)}
                        className="px-6 py-3.5 rounded-xl border border-gray-200 text-[#475569] font-semibold text-sm hover:border-[#1D8BF8] hover:text-[#1D8BF8] transition"
                      >
                        Annuleren
                      </button>
                      <button
                        onClick={handleSave}
                        disabled={loading}
                        className="group relative"
                      >
                        <div className="relative px-6 py-3.5 bg-[#3EB489] text-white rounded-xl font-semibold text-sm hover:bg-[#359E78] transition inline-flex items-center shadow-sm shadow-[#3EB489]/20">
                          {loading ? (
                            <>
                              <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2"></div>
                              Opslaan...
                            </>
                          ) : (
                            <>
                              <Save className="w-4 h-4 mr-2" />
                              Opslaan
                            </>
                          )}
                        </div>
                      </button>
                    </div>
                  )}
                </div>
              )}

              {/* Portfolio Tab */}
              {activeTab === 'portfolio' && (
                <div>
                  {user.role === 'creator' ? (
                    <div>
                      {/* Portfolio Header */}
                      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-8">
                        <div className="min-w-0">
                          <h3 className="text-xl md:text-2xl font-bold mb-2 text-[#0F172A]">Mijn Portfolio</h3>
                          <p className="text-[#475569]">Showcase je beste werk en laat brands zien wat je kunt</p>
                        </div>
                        <button
                          onClick={() => setShowPortfolioModal(true)}
                          className="group relative self-start flex-shrink-0"
                        >
                          <div className="relative px-6 py-3.5 bg-[#3EB489] text-white rounded-xl font-semibold text-sm hover:bg-[#359E78] transition inline-flex items-center shadow-sm shadow-[#3EB489]/20">
                            <Plus className="w-4 h-4 mr-2" />
                            Item Toevoegen
                          </div>
                        </button>
                      </div>

                      {/* Portfolio Grid */}
                      {user.portfolio && user.portfolio.length > 0 ? (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                          {user.portfolio.map((item) => (
                            <div key={item._id} className="relative group">
                              <div className="relative bg-white border border-gray-100 shadow-md rounded-2xl overflow-hidden">
                                {/* Video/Thumbnail */}
                                <div className="aspect-video bg-gray-100 relative">
                                  {item.thumbnailUrl ? (
                                    <img
                                      src={item.thumbnailUrl}
                                      alt={item.title}
                                      className="w-full h-full object-cover"
                                    />
                                  ) : (
                                    <div className="w-full h-full flex items-center justify-center">
                                      <Video className="w-12 h-12 text-[#475569]/30" />
                                    </div>
                                  )}

                                  {/* Video Type Indicator */}
                                  <div className="absolute top-3 right-3">
                                    {(item.videoUrl || '').includes('cloudinary.com') || (item.videoUrl || '').includes('/uploads/') ? (
                                      <span className="px-2 py-1 bg-green-500/80 rounded-lg text-xs font-medium text-white">
                                        Geüpload
                                      </span>
                                    ) : (
                                      <span className="px-2 py-1 bg-blue-500/80 rounded-lg text-xs font-medium text-white">
                                        Link
                                      </span>
                                    )}
                                  </div>

                                  {/* Hover Actions (alleen desktop — hover bestaat niet op touch) */}
                                  <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity hidden md:flex items-center justify-center gap-3">
                                    <a
                                      href={item.videoUrl}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="p-2 bg-black/20 rounded-lg hover:bg-black/30 transition"
                                      title={(item.videoUrl || '').includes('cloudinary.com') || (item.videoUrl || '').includes('/uploads/') ? 'Video bekijken' : 'Externe link openen'}
                                    >
                                      <Eye className="w-5 h-5 text-white" />
                                    </a>
                                    <button
                                      onClick={() => handleEditPortfolioItem(item)}
                                      className="p-2 bg-black/20 rounded-lg hover:bg-black/30 transition"
                                    >
                                      <Edit className="w-5 h-5 text-white" />
                                    </button>
                                    <button
                                      onClick={() => handlePortfolioDelete(item._id)}
                                      className="p-2 bg-red-500/20 rounded-lg hover:bg-red-500/30 transition"
                                    >
                                      <Trash2 className="w-5 h-5 text-red-400" />
                                    </button>
                                  </div>

                                  {/* Actieknoppen mobiel: altijd zichtbaar rechtsonder (44px tikdoelen, verwijderen apart gezet) */}
                                  <div className="absolute bottom-2 right-2 flex gap-2 md:hidden">
                                    <a
                                      href={item.videoUrl}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="p-2.5 bg-black/50 rounded-lg active:bg-black/70 transition"
                                      aria-label="Video bekijken"
                                    >
                                      <Eye className="w-5 h-5 text-white" />
                                    </a>
                                    <button
                                      onClick={() => handleEditPortfolioItem(item)}
                                      className="p-2.5 bg-black/50 rounded-lg active:bg-black/70 transition"
                                      aria-label="Bewerken"
                                    >
                                      <Edit className="w-5 h-5 text-white" />
                                    </button>
                                    <button
                                      onClick={() => handlePortfolioDelete(item._id)}
                                      className="p-2.5 ml-1 bg-red-500/70 rounded-lg active:bg-red-500/90 transition"
                                      aria-label="Verwijderen"
                                    >
                                      <Trash2 className="w-5 h-5 text-white" />
                                    </button>
                                  </div>

                                  {/* Category Badge */}
                                  {item.category && (
                                    <div className="absolute top-3 left-3">
                                      <span className="px-2 py-1 bg-black/50 rounded-lg text-xs font-medium text-white">
                                        {item.category}
                                      </span>
                                    </div>
                                  )}
                                </div>

                                {/* Content */}
                                <div className="p-4">
                                  <h4 className="font-semibold text-[#0F172A] mb-2 line-clamp-2">{item.title}</h4>
                                  {item.description && (
                                    <p className="text-[#475569] text-sm line-clamp-3">{item.description}</p>
                                  )}
                                  <div className="mt-3 text-xs text-[#475569]/60">
                                    {new Date(item.createdAt).toLocaleDateString('nl-NL')}
                                  </div>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="text-center py-16">
                          <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-blue-50 flex items-center justify-center">
                            <Folder className="w-10 h-10 text-[#1D8BF8]" />
                          </div>
                          <h3 className="text-xl font-bold mb-2 text-[#0F172A]">Nog geen portfolio items</h3>
                          <p className="text-[#475569] mb-6 max-w-md mx-auto">
                            Upload je eerste video en laat zien wat je kunt!
                          </p>
                          <button
                            onClick={() => setShowPortfolioModal(true)}
                            className="group relative"
                          >
                            <div className="relative px-6 py-3.5 bg-[#3EB489] text-white rounded-xl font-semibold text-sm hover:bg-[#359E78] transition inline-flex items-center shadow-sm shadow-[#3EB489]/20">
                              <Plus className="w-4 h-4 mr-2" />
                              Eerste Item Toevoegen
                            </div>
                          </button>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="text-center py-16">
                      <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-blue-50 flex items-center justify-center">
                        <Folder className="w-10 h-10 text-[#1D8BF8]" />
                      </div>
                      <h3 className="text-2xl font-bold mb-2 text-[#0F172A]">Portfolio niet beschikbaar</h3>
                      <p className="text-[#475569] mb-8 max-w-md mx-auto">
                        Portfolio functionaliteit is alleen beschikbaar voor creators
                      </p>
                    </div>
                  )}
                </div>
              )}

              {/* Moodboard Tab */}
              {activeTab === 'moodboard' && (
                <div>
                  {user.role === 'creator' ? (
                    <div>
                      {/* Moodboard Header */}
                      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 sm:gap-4 mb-8">
                        <div className="min-w-0">
                          <h3 className="text-xl md:text-2xl font-bold mb-2 text-[#0F172A]">Mijn Moodboard</h3>
                          <p className="text-[#475569]">Upload foto's die jouw stijl en vibe laten zien aan bedrijven</p>
                        </div>
                        <div className="text-sm text-[#475569] font-medium whitespace-nowrap">
                          {user.moodboard?.length || 0}/12 foto's
                        </div>
                      </div>

                      {/* Upload Area */}
                      {(!user.moodboard || user.moodboard.length < 12) && (
                        <div className="mb-8">
                          <label className="block">
                            <div className={`relative border-2 border-dashed rounded-2xl p-8 text-center cursor-pointer transition-all ${
                              uploadingMoodboard
                                ? 'border-[#1D8BF8] bg-blue-50'
                                : 'border-gray-300 hover:border-[#1D8BF8] hover:bg-blue-50/50'
                            }`}>
                              {uploadingMoodboard ? (
                                <div className="flex flex-col items-center gap-2">
                                  <div className="w-10 h-10 border-3 border-[#1D8BF8] border-t-transparent rounded-full animate-spin"></div>
                                  <p className="text-[#1D8BF8] font-medium">Uploaden... {uploadProgress > 0 ? `${uploadProgress}%` : ''}</p>
                                  {uploadProgress > 0 && (
                                    <div className="w-48 bg-gray-200 rounded-full h-1.5">
                                      <div className="bg-[#3EB489] h-1.5 rounded-full transition-all" style={{ width: `${uploadProgress}%` }} />
                                    </div>
                                  )}
                                </div>
                              ) : (
                                <div className="flex flex-col items-center">
                                  <div className="w-14 h-14 rounded-2xl bg-blue-50 flex items-center justify-center mb-3">
                                    <Camera className="w-7 h-7 text-[#1D8BF8]" />
                                  </div>
                                  <p className="font-semibold text-[#0F172A] mb-1">Klik om foto's te uploaden</p>
                                  <p className="text-sm text-[#475569]">Meerdere selecteren mogelijk - JPG, PNG of WebP - max 5MB per foto</p>
                                </div>
                              )}
                              <input
                                type="file"
                                accept="image/jpeg,image/png,image/webp"
                                multiple
                                onChange={handleMoodboardUpload}
                                className="hidden"
                                disabled={uploadingMoodboard}
                              />
                            </div>
                          </label>
                        </div>
                      )}

                      {/* Moodboard Grid */}
                      {user.moodboard && user.moodboard.length > 0 ? (
                        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                          {user.moodboard.map((item) => (
                            <div key={item._id} className="relative group">
                              <div className="relative aspect-square rounded-2xl overflow-hidden bg-gray-100 border border-gray-200 shadow-sm">
                                <img
                                  src={item.imageUrl}
                                  alt={item.caption || 'Moodboard foto'}
                                  className="w-full h-full object-cover"
                                />
                                {/* Hover overlay (alleen desktop — hover bestaat niet op touch) */}
                                <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity hidden md:flex items-center justify-center gap-2">
                                  <button
                                    onClick={() => {
                                      setEditingMoodboardItem(item._id)
                                      setEditingCaption(item.caption || '')
                                    }}
                                    className="p-2 bg-white/20 rounded-xl hover:bg-white/30 transition"
                                    title="Bijschrift bewerken"
                                  >
                                    <Edit className="w-5 h-5 text-white" />
                                  </button>
                                  <button
                                    onClick={() => handleMoodboardDelete(item._id)}
                                    className="p-2 bg-red-500/30 rounded-xl hover:bg-red-500/40 transition"
                                    title="Verwijderen"
                                  >
                                    <Trash2 className="w-5 h-5 text-white" />
                                  </button>
                                </div>
                                {/* Actieknoppen mobiel: altijd zichtbaar rechtsonder (44px tikdoelen, verwijderen apart gezet) */}
                                <div className="absolute bottom-2 right-2 flex gap-2 md:hidden">
                                  <button
                                    onClick={() => {
                                      setEditingMoodboardItem(item._id)
                                      setEditingCaption(item.caption || '')
                                    }}
                                    className="p-2.5 bg-black/50 rounded-lg active:bg-black/70 transition"
                                    aria-label="Bijschrift bewerken"
                                  >
                                    <Edit className="w-5 h-5 text-white" />
                                  </button>
                                  <button
                                    onClick={() => handleMoodboardDelete(item._id)}
                                    className="p-2.5 ml-1 bg-red-500/70 rounded-lg active:bg-red-500/90 transition"
                                    aria-label="Verwijderen"
                                  >
                                    <Trash2 className="w-5 h-5 text-white" />
                                  </button>
                                </div>
                              </div>
                              {/* Caption. Mobiel: input op eigen regel (cel is ~140px), knoppen eronder; 16px tekst tegen iOS-zoom. */}
                              {editingMoodboardItem === item._id ? (
                                <div className="mt-2 flex flex-wrap gap-2">
                                  <input
                                    type="text"
                                    value={editingCaption}
                                    onChange={(e) => setEditingCaption(e.target.value)}
                                    maxLength={150}
                                    className="basis-full sm:basis-auto sm:flex-1 min-w-0 text-base sm:text-sm px-3 py-2 sm:py-1.5 rounded-lg border border-gray-300 focus:border-[#1D8BF8] focus:ring-1 focus:ring-[#1D8BF8] outline-none"
                                    placeholder="Bijschrift..."
                                    autoFocus
                                  />
                                  <button
                                    onClick={() => handleMoodboardCaptionSave(item._id)}
                                    className="flex-1 sm:flex-none inline-flex items-center justify-center min-h-[40px] sm:min-h-0 px-3 py-1.5 bg-[#3EB489] text-white rounded-lg text-sm font-medium hover:bg-[#359E78] transition"
                                    aria-label="Bijschrift opslaan"
                                  >
                                    <Save className="w-4 h-4" />
                                  </button>
                                  <button
                                    onClick={() => { setEditingMoodboardItem(null); setEditingCaption('') }}
                                    className="flex-1 sm:flex-none inline-flex items-center justify-center min-h-[40px] sm:min-h-0 px-3 py-1.5 bg-gray-100 text-[#475569] rounded-lg text-sm hover:bg-gray-200 transition"
                                    aria-label="Annuleren"
                                  >
                                    <X className="w-4 h-4" />
                                  </button>
                                </div>
                              ) : (
                                item.caption && (
                                  <p className="mt-2 text-sm text-[#475569] line-clamp-2">{item.caption}</p>
                                )
                              )}
                            </div>
                          ))}
                        </div>
                      ) : (
                        !uploadingMoodboard && (
                          <div className="text-center py-16">
                            <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-blue-50 flex items-center justify-center">
                              <Image className="w-10 h-10 text-[#1D8BF8]" />
                            </div>
                            <h3 className="text-xl font-bold mb-2 text-[#0F172A]">Nog geen moodboard foto's</h3>
                            <p className="text-[#475569] mb-6 max-w-md mx-auto">
                              Upload foto's die jouw stijl, eerdere werk of inspiratie laten zien
                            </p>
                          </div>
                        )
                      )}
                    </div>
                  ) : (
                    <div className="text-center py-16">
                      <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-blue-50 flex items-center justify-center">
                        <Image className="w-10 h-10 text-[#1D8BF8]" />
                      </div>
                      <h3 className="text-2xl font-bold mb-2 text-[#0F172A]">Moodboard niet beschikbaar</h3>
                      <p className="text-[#475569]">Moodboard functionaliteit is alleen beschikbaar voor creators</p>
                    </div>
                  )}
                </div>
              )}

              {/* Packages Tab (Creator) */}
              {activeTab === 'packages' && user.role === 'creator' && (() => {
                const tierStyles = [
                  { border: 'border-[#1D8BF8]', bg: 'bg-blue-50', text: 'text-[#1D8BF8]', badge: 'bg-[#3EB489]', label: 'Basis' },
                  { border: 'border-purple-500', bg: 'bg-purple-50', text: 'text-purple-600', badge: 'bg-purple-500', label: 'Standaard' },
                  { border: 'border-amber-500', bg: 'bg-amber-50', text: 'text-amber-600', badge: 'bg-amber-500', label: 'Premium' }
                ]
                const includedItemOptions = [
                  { value: 'muziek', label: 'Muziek', icon: Music },
                  { value: 'script_briefing', label: 'Script/Briefing', icon: FileText },
                  { value: 'ruwe_content', label: 'Ruwe content', icon: Film },
                  { value: 'video_editing', label: 'Video editing', icon: Video },
                  { value: 'ondertiteling', label: 'Ondertiteling', icon: Type },
                  { value: 'voice_over', label: 'Voice-over', icon: Mic },
                  { value: 'productfotos', label: 'Productfoto\'s', icon: Camera },
                  { value: 'thumbnail', label: 'Thumbnail', icon: Image }
                ]
                const newPackage = { name: '', price: 25, description: '', videoCount: 1, videoLength: '30s', deliveryDays: '6-8 dagen', revisions: 1, hooks: 1, includedItems: [] }
                const updatePkg = (index, field, value) => {
                  const newPackages = [...formData.packages]
                  newPackages[index] = { ...newPackages[index], [field]: value }
                  setFormData(prev => ({ ...prev, packages: newPackages }))
                }
                return (
                <div>
                  <PackagesTour
                    ref={packagesTourRef}
                    hasPackages={formData.packages.length > 0}
                    onAddDefaultPackage={() => setFormData(prev => ({ ...prev, packages: prev.packages.length === 0 ? [{ ...newPackage }] : prev.packages }))}
                  />
                  {/* Header */}
                  <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
                    <div>
                      <h3 className="text-2xl font-bold mb-1 text-[#0F172A]">Pakketten</h3>
                      <p className="text-[#475569] text-sm">Stel maximaal 3 pakketten samen die bedrijven direct kunnen boeken</p>
                    </div>
                    <div className="flex items-center gap-3">
                      {formData.packages.length > 0 && (
                        <div className="flex gap-1">
                          {[0, 1, 2].map(i => (
                            <div key={i} className={`w-2.5 h-2.5 rounded-full transition-all ${i < formData.packages.length ? tierStyles[i].badge : 'bg-gray-200'}`} />
                          ))}
                        </div>
                      )}
                      <span className="text-xs text-[#475569] font-medium">{formData.packages.length}/3</span>
                      <button
                        onClick={() => packagesTourRef.current?.restartTour()}
                        className="p-2.5 rounded-xl border border-gray-200 bg-white hover:bg-sky-50 hover:border-sky-300 text-[#94a3b8] hover:text-[#0ea5e9] transition-all"
                        title="Hulp: tour opnieuw starten"
                        aria-label="Hulp: tour opnieuw starten"
                      >
                        <HelpCircle className="w-5 h-5" />
                      </button>
                    </div>
                  </div>

                  {formData.packages.length === 0 ? (
                    <div className="text-center py-20">
                      <div className="relative w-24 h-24 mx-auto mb-8">
                        <div className="absolute inset-0 bg-blue-100 rounded-2xl rotate-6"></div>
                        <div className="absolute inset-0 bg-blue-50 rounded-2xl -rotate-3"></div>
                        <div className="relative w-full h-full bg-white rounded-2xl border-2 border-dashed border-[#1D8BF8]/30 flex items-center justify-center">
                          <Package className="w-10 h-10 text-[#1D8BF8]" />
                        </div>
                      </div>
                      <h3 className="text-2xl font-bold mb-3 text-[#0F172A]">Nog geen pakketten</h3>
                      <p className="text-[#475569] mb-8 max-w-md mx-auto leading-relaxed">
                        Maak pakketten aan zodat bedrijven direct kunnen zien wat je aanbiedt en wat het kost
                      </p>
                      <button
                        data-tour="pkg-add"
                        onClick={() => setFormData(prev => ({ ...prev, packages: [{ ...newPackage }] }))}
                        className="px-8 py-3.5 bg-[#3EB489] text-white rounded-2xl font-semibold hover:bg-[#359E78] transition inline-flex items-center gap-2 shadow-lg shadow-blue-500/25"
                      >
                        <Plus className="w-5 h-5" />
                        Eerste pakket aanmaken
                      </button>
                    </div>
                  ) : (
                    <div className="space-y-6">
                      {/* Package Cards Grid */}
                      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
                        {formData.packages.map((pkg, index) => {
                          const style = tierStyles[index]
                          return (
                            <div key={index} className={`relative rounded-2xl border-2 ${style.border} bg-white overflow-hidden transition-shadow hover:shadow-lg`}>
                              {/* Card Header */}
                              <div className={`${style.bg} px-5 py-4 flex items-center justify-between`}>
                                <div className="flex items-center gap-3">
                                  <div className={`w-8 h-8 ${style.badge} rounded-lg flex items-center justify-center`}>
                                    <Package className="w-4 h-4 text-white" />
                                  </div>
                                  <div>
                                    <p className={`text-xs font-semibold ${style.text} uppercase tracking-wider`}>Pakket {index + 1}</p>
                                    <p className="text-xs text-[#475569]">{style.label}</p>
                                  </div>
                                </div>
                                <button
                                  onClick={() => setFormData(prev => ({ ...prev, packages: prev.packages.filter((_, i) => i !== index) }))}
                                  className="p-1.5 hover:bg-red-100 rounded-lg transition text-[#475569] hover:text-red-500"
                                  title="Pakket verwijderen"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </div>

                              {/* Card Body */}
                              <div className="p-5 space-y-4">
                                {/* Name & Price */}
                                <div data-tour={index === 0 ? 'pkg-name-price' : undefined} className="space-y-4">
                                <div>
                                  <label className="block text-xs font-semibold text-[#475569] uppercase tracking-wider mb-1.5">Naam</label>
                                  <input
                                    type="text"
                                    value={pkg.name}
                                    onChange={(e) => updatePkg(index, 'name', e.target.value)}
                                    placeholder={style.label}
                                    className={`w-full bg-[#F8FAFC] border ${pkg.name?.trim() ? 'border-gray-200' : 'border-red-300'} rounded-xl px-3.5 py-2.5 text-base sm:text-sm focus:ring-2 focus:ring-[#1D8BF8]/30 focus:border-[#1D8BF8] text-[#0F172A] font-semibold placeholder:font-normal placeholder:text-gray-300`}
                                  />
                                  {!pkg.name?.trim() && (
                                    <p className="mt-1.5 text-xs text-red-500">Naam is verplicht — vul bijv. "{style.label}" in.</p>
                                  )}
                                </div>

                                {/* Price */}
                                <div>
                                  <label className="block text-xs font-semibold text-[#475569] uppercase tracking-wider mb-1.5">Prijs</label>
                                  <div className="relative">
                                    <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-sm font-bold text-[#475569]">&euro;</span>
                                    <input
                                      type="number"
                                      min="25"
                                      value={pkg.price}
                                      onChange={(e) => updatePkg(index, 'price', parseInt(e.target.value) || 25)}
                                      className="w-full bg-[#F8FAFC] border border-gray-200 rounded-xl pl-8 pr-20 py-2.5 text-base sm:text-sm focus:ring-2 focus:ring-[#1D8BF8]/30 focus:border-[#1D8BF8] text-[#0F172A] font-semibold"
                                    />
                                    <span className="absolute right-3.5 top-1/2 -translate-y-1/2 text-xs text-[#94a3b8]">excl. BTW</span>
                                  </div>
                                </div>
                                </div>

                                {/* Description */}
                                <div>
                                  <label className="block text-xs font-semibold text-[#475569] uppercase tracking-wider mb-1.5">Beschrijving</label>
                                  <textarea
                                    value={pkg.description}
                                    onChange={(e) => updatePkg(index, 'description', e.target.value)}
                                    maxLength={200}
                                    rows={2}
                                    placeholder="Korte omschrijving..."
                                    className="w-full bg-[#F8FAFC] border border-gray-200 rounded-xl px-3.5 py-2.5 text-base sm:text-sm focus:ring-2 focus:ring-[#1D8BF8]/30 focus:border-[#1D8BF8] text-[#0F172A] resize-none"
                                  />
                                  <p className="text-[10px] text-[#94a3b8] mt-0.5 text-right">{pkg.description?.length || 0}/200</p>
                                </div>

                                {/* Specs Grid */}
                                <div data-tour={index === 0 ? 'pkg-specs' : undefined}>
                                  <label className="block text-xs font-semibold text-[#475569] uppercase tracking-wider mb-2">Specificaties</label>
                                  <div className="grid grid-cols-2 gap-2.5">
                                    {/* Video Count */}
                                    <div className="flex items-center gap-2 min-w-0 bg-[#F8FAFC] rounded-xl px-3 py-2.5 border border-gray-100">
                                      <Video className="w-4 h-4 text-[#94a3b8] flex-shrink-0" />
                                      <input
                                        type="number"
                                        min="1"
                                        value={pkg.videoCount}
                                        onChange={(e) => updatePkg(index, 'videoCount', parseInt(e.target.value) || 1)}
                                        className="w-full min-w-0 bg-transparent text-base sm:text-sm font-medium text-[#0F172A] focus:outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                                      />
                                      <span className="text-[10px] text-[#94a3b8] whitespace-nowrap">video's</span>
                                    </div>

                                    {/* Video Length */}
                                    <div className="flex items-center gap-2 min-w-0 bg-[#F8FAFC] rounded-xl px-3 py-2.5 border border-gray-100">
                                      <Film className="w-4 h-4 text-[#94a3b8] flex-shrink-0" />
                                      <select
                                        value={pkg.videoLength}
                                        onChange={(e) => updatePkg(index, 'videoLength', e.target.value)}
                                        className="w-full min-w-0 bg-transparent text-base sm:text-sm font-medium text-[#0F172A] focus:outline-none cursor-pointer"
                                      >
                                        <option value="15s">15s</option>
                                        <option value="30s">30s</option>
                                        <option value="60s">60s</option>
                                        <option value="90s">90s</option>
                                        <option value="2min">2 min</option>
                                        <option value="3min+">3+ min</option>
                                      </select>
                                    </div>

                                    {/* Delivery */}
                                    <div className="flex items-center gap-2 min-w-0 bg-[#F8FAFC] rounded-xl px-3 py-2.5 border border-gray-100">
                                      <Clock className="w-4 h-4 text-[#94a3b8] flex-shrink-0" />
                                      <select
                                        value={pkg.deliveryDays}
                                        onChange={(e) => updatePkg(index, 'deliveryDays', e.target.value)}
                                        className="w-full min-w-0 bg-transparent text-base sm:text-sm font-medium text-[#0F172A] focus:outline-none cursor-pointer"
                                      >
                                        <option value="3-5 dagen">3-5 d</option>
                                        <option value="6-8 dagen">6-8 d</option>
                                        <option value="9-14 dagen">9-14 d</option>
                                        <option value="2-3 weken">2-3 wk</option>
                                      </select>
                                    </div>

                                    {/* Revisions */}
                                    <div className="flex items-center gap-2 min-w-0 bg-[#F8FAFC] rounded-xl px-3 py-2.5 border border-gray-100">
                                      <RotateCcw className="w-4 h-4 text-[#94a3b8] flex-shrink-0" />
                                      <input
                                        type="number"
                                        min="0"
                                        value={pkg.revisions}
                                        onChange={(e) => updatePkg(index, 'revisions', parseInt(e.target.value) || 0)}
                                        className="w-full min-w-0 bg-transparent text-base sm:text-sm font-medium text-[#0F172A] focus:outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                                      />
                                      <span className="text-[10px] text-[#94a3b8] whitespace-nowrap">revisies</span>
                                    </div>

                                    {/* Hooks */}
                                    <div className="col-span-2 flex items-center gap-2 bg-[#F8FAFC] rounded-xl px-3 py-2.5 border border-gray-100">
                                      <Sparkles className="w-4 h-4 text-[#94a3b8] flex-shrink-0" />
                                      <input
                                        type="number"
                                        min="0"
                                        value={pkg.hooks}
                                        onChange={(e) => updatePkg(index, 'hooks', parseInt(e.target.value) || 0)}
                                        className="w-12 bg-transparent text-base sm:text-sm font-medium text-[#0F172A] focus:outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                                      />
                                      <span className="text-[10px] text-[#94a3b8]">hooks (variaties opening)</span>
                                    </div>
                                  </div>
                                </div>

                                {/* Included Items */}
                                <div data-tour={index === 0 ? 'pkg-included' : undefined}>
                                  <label className="block text-xs font-semibold text-[#475569] uppercase tracking-wider mb-2">Inclusief</label>
                                  <div className="flex flex-wrap gap-2">
                                    {includedItemOptions.map(item => {
                                      const isChecked = pkg.includedItems?.includes(item.value) || false
                                      const ItemIcon = item.icon
                                      return (
                                        <button
                                          key={item.value}
                                          type="button"
                                          onClick={() => {
                                            const currentItems = pkg.includedItems || []
                                            const newItems = isChecked
                                              ? currentItems.filter(i => i !== item.value)
                                              : [...currentItems, item.value]
                                            updatePkg(index, 'includedItems', newItems)
                                          }}
                                          className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all border ${
                                            isChecked
                                              ? `${style.bg} ${style.border} ${style.text}`
                                              : 'bg-white border-gray-200 text-[#94a3b8] hover:border-gray-300 hover:text-[#475569]'
                                          }`}
                                        >
                                          {isChecked ? (
                                            <CheckCircle className="w-3.5 h-3.5" />
                                          ) : (
                                            <ItemIcon className="w-3.5 h-3.5" />
                                          )}
                                          {item.label}
                                        </button>
                                      )
                                    })}
                                  </div>
                                </div>
                              </div>
                            </div>
                          )
                        })}

                        {/* Add Package Card */}
                        {formData.packages.length < 3 && (
                          <button
                            data-tour="pkg-add"
                            onClick={() => setFormData(prev => ({ ...prev, packages: [...prev.packages, { ...newPackage }] }))}
                            className="rounded-2xl border-2 border-dashed border-gray-200 bg-[#F8FAFC] hover:border-[#1D8BF8] hover:bg-blue-50/50 transition-all min-h-[160px] lg:min-h-[400px] flex flex-col items-center justify-center gap-3 group"
                          >
                            <div className="w-12 h-12 rounded-xl bg-white border border-gray-200 group-hover:border-[#1D8BF8] group-hover:bg-blue-50 flex items-center justify-center transition-all">
                              <Plus className="w-6 h-6 text-gray-300 group-hover:text-[#1D8BF8] transition-colors" />
                            </div>
                            <div className="text-center">
                              <p className="font-semibold text-sm text-[#475569] group-hover:text-[#1D8BF8] transition-colors">Pakket toevoegen</p>
                              <p className="text-xs text-[#94a3b8] mt-0.5">{tierStyles[formData.packages.length]?.label}</p>
                            </div>
                          </button>
                        )}
                      </div>

                      {/* Save Button */}
                      <div className="flex justify-end pt-2">
                        <button
                          data-tour="pkg-save"
                          onClick={handleSave}
                          disabled={loading}
                          className="px-8 py-3.5 bg-[#3EB489] text-white rounded-2xl font-semibold hover:bg-[#359E78] transition inline-flex items-center gap-2 shadow-lg shadow-blue-500/20 disabled:opacity-60"
                        >
                          {loading ? (
                            <>
                              <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                              Opslaan...
                            </>
                          ) : (
                            <>
                              <Save className="w-4 h-4" />
                              Pakketten opslaan
                            </>
                          )}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )})()}

              {/* Brands Tab (Agency) */}
              {activeTab === 'brands' && user.role === 'agency' && (
                <div>
                  <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-8">
                    <div className="min-w-0">
                      <h3 className="text-xl md:text-2xl font-bold mb-2 text-[#0F172A]">Beheerde Merken</h3>
                      <p className="text-[#475569]">Beheer de merken waarvoor je agency werkt</p>
                    </div>
                    <button
                      onClick={() => { setShowBrandForm(true); setEditingBrandIndex(null); setBrandFormData({ name: '', logo: '', website: '' }) }}
                      className="group relative self-start flex-shrink-0"
                    >
                      <div className="relative px-6 py-3.5 bg-[#3EB489] text-white rounded-xl font-semibold text-sm hover:bg-[#359E78] transition inline-flex items-center shadow-sm shadow-[#3EB489]/20">
                        <Plus className="w-4 h-4 mr-2" />
                        Merk Toevoegen
                      </div>
                    </button>
                  </div>

                  {showBrandForm && (
                    <div className="mb-8 p-6 bg-[#F8FAFC] border border-gray-200 rounded-2xl">
                      <h4 className="font-semibold text-[#0F172A] mb-4">{editingBrandIndex !== null ? 'Merk Bewerken' : 'Nieuw Merk'}</h4>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                        <div>
                          <label className="block text-[11px] font-semibold uppercase tracking-wider text-[#94A3B8] mb-2.5">Merknaam</label>
                          <input
                            type="text"
                            value={brandFormData.name}
                            onChange={(e) => setBrandFormData(prev => ({ ...prev, name: e.target.value }))}
                            className="w-full bg-white border border-gray-200 rounded-xl px-4 py-3 focus:ring-2 focus:ring-[#1D8BF8]/50 focus:border-[#1D8BF8] text-[#0F172A]"
                            placeholder="Merknaam"
                          />
                        </div>
                        <div>
                          <label className="block text-[11px] font-semibold uppercase tracking-wider text-[#94A3B8] mb-2.5">Logo URL</label>
                          <input
                            type="text"
                            value={brandFormData.logo}
                            onChange={(e) => setBrandFormData(prev => ({ ...prev, logo: e.target.value }))}
                            className="w-full bg-white border border-gray-200 rounded-xl px-4 py-3 focus:ring-2 focus:ring-[#1D8BF8]/50 focus:border-[#1D8BF8] text-[#0F172A]"
                            placeholder="https://..."
                          />
                        </div>
                        <div>
                          <label className="block text-[11px] font-semibold uppercase tracking-wider text-[#94A3B8] mb-2.5">Website</label>
                          <input
                            type="url"
                            value={brandFormData.website}
                            onChange={(e) => setBrandFormData(prev => ({ ...prev, website: e.target.value }))}
                            className="w-full bg-white border border-gray-200 rounded-xl px-4 py-3 focus:ring-2 focus:ring-[#1D8BF8]/50 focus:border-[#1D8BF8] text-[#0F172A]"
                            placeholder="https://..."
                          />
                        </div>
                      </div>
                      <div className="flex gap-3">
                        <button
                          onClick={() => { setShowBrandForm(false); setEditingBrandIndex(null) }}
                          className="px-4 py-2 bg-white text-[#475569] border border-gray-200 rounded-xl font-medium hover:bg-gray-50 transition"
                        >
                          Annuleren
                        </button>
                        <button
                          onClick={handleBrandSave}
                          disabled={loading || !brandFormData.name}
                          className="px-4 py-2 bg-[#3EB489] text-white rounded-xl font-medium hover:bg-[#359E78] transition disabled:opacity-50 inline-flex items-center"
                        >
                          {loading ? (
                            <>
                              <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2" />
                              Opslaan...
                            </>
                          ) : (
                            <>
                              <Save className="w-4 h-4 mr-2" />
                              Opslaan
                            </>
                          )}
                        </button>
                      </div>
                    </div>
                  )}

                  {formData.managedBrands.length > 0 ? (
                    <div className="space-y-4">
                      {formData.managedBrands.map((brand, index) => (
                        <div key={index} className="flex items-center justify-between gap-3 p-4 bg-[#F8FAFC] border border-gray-200 rounded-2xl">
                          <div className="flex items-center gap-4 min-w-0 flex-1">
                            {brand.logo ? (
                              <img src={brand.logo} alt={brand.name} className="w-12 h-12 rounded-xl object-cover border border-gray-200 flex-shrink-0" />
                            ) : (
                              <div className="w-12 h-12 rounded-xl bg-white border border-gray-200 flex items-center justify-center flex-shrink-0">
                                <Building className="w-6 h-6 text-[#475569]/30" />
                              </div>
                            )}
                            <div className="min-w-0">
                              <h4 className="font-semibold text-[#0F172A] break-words">{brand.name}</h4>
                              {brand.website && (
                                <a href={brand.website} target="_blank" rel="noopener noreferrer" className="text-sm text-[#1D8BF8] hover:text-[#1670c9] flex items-center gap-1 min-w-0 max-w-full">
                                  <span className="truncate">{brand.website}</span> <ExternalLink className="w-3 h-3 flex-shrink-0" />
                                </a>
                              )}
                            </div>
                          </div>
                          <div className="flex gap-2 flex-shrink-0">
                            <button
                              onClick={() => { setEditingBrandIndex(index); setBrandFormData(brand); setShowBrandForm(true) }}
                              className="p-2.5 hover:bg-gray-100 rounded-xl transition"
                              aria-label="Merk bewerken"
                            >
                              <Edit className="w-4 h-4 text-[#475569]" />
                            </button>
                            <button
                              onClick={() => handleBrandDelete(index)}
                              className="p-2.5 hover:bg-red-50 rounded-xl transition"
                              aria-label="Merk verwijderen"
                            >
                              <Trash2 className="w-4 h-4 text-red-400" />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-16">
                      <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-blue-50 flex items-center justify-center">
                        <Building className="w-10 h-10 text-[#1D8BF8]" />
                      </div>
                      <h3 className="text-xl font-bold mb-2 text-[#0F172A]">Nog geen merken toegevoegd</h3>
                      <p className="text-[#475569] mb-6 max-w-md mx-auto">
                        Voeg de merken toe waarvoor je agency werkt
                      </p>
                    </div>
                  )}
                </div>
              )}

              {/* Team Tab (Agency) */}
              {activeTab === 'team' && user.role === 'agency' && (
                <div>
                  <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-8">
                    <div className="min-w-0">
                      <h3 className="text-xl md:text-2xl font-bold mb-2 text-[#0F172A]">Teamleden</h3>
                      <p className="text-[#475569]">Beheer je agency teamleden</p>
                    </div>
                    <button
                      onClick={() => { setShowTeamForm(true); setEditingTeamIndex(null); setTeamFormData({ name: '', email: '', role: 'member' }) }}
                      className="group relative self-start flex-shrink-0"
                    >
                      <div className="relative px-6 py-3.5 bg-[#3EB489] text-white rounded-xl font-semibold text-sm hover:bg-[#359E78] transition inline-flex items-center shadow-sm shadow-[#3EB489]/20">
                        <Plus className="w-4 h-4 mr-2" />
                        Teamlid Toevoegen
                      </div>
                    </button>
                  </div>

                  {showTeamForm && (
                    <div className="mb-8 p-6 bg-[#F8FAFC] border border-gray-200 rounded-2xl">
                      <h4 className="font-semibold text-[#0F172A] mb-4">{editingTeamIndex !== null ? 'Teamlid Bewerken' : 'Nieuw Teamlid'}</h4>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                        <div>
                          <label className="block text-[11px] font-semibold uppercase tracking-wider text-[#94A3B8] mb-2.5">Naam</label>
                          <input
                            type="text"
                            value={teamFormData.name}
                            onChange={(e) => setTeamFormData(prev => ({ ...prev, name: e.target.value }))}
                            className="w-full bg-white border border-gray-200 rounded-xl px-4 py-3 focus:ring-2 focus:ring-[#1D8BF8]/50 focus:border-[#1D8BF8] text-[#0F172A]"
                            placeholder="Volledige naam"
                          />
                        </div>
                        <div>
                          <label className="block text-[11px] font-semibold uppercase tracking-wider text-[#94A3B8] mb-2.5">E-mail</label>
                          <input
                            type="email"
                            value={teamFormData.email}
                            onChange={(e) => setTeamFormData(prev => ({ ...prev, email: e.target.value }))}
                            className="w-full bg-white border border-gray-200 rounded-xl px-4 py-3 focus:ring-2 focus:ring-[#1D8BF8]/50 focus:border-[#1D8BF8] text-[#0F172A]"
                            placeholder="email@voorbeeld.nl"
                          />
                        </div>
                        <div>
                          <label className="block text-[11px] font-semibold uppercase tracking-wider text-[#94A3B8] mb-2.5">Rol</label>
                          <select
                            value={teamFormData.role}
                            onChange={(e) => setTeamFormData(prev => ({ ...prev, role: e.target.value }))}
                            className="w-full bg-white border border-gray-200 rounded-xl px-4 py-3 focus:ring-2 focus:ring-[#1D8BF8]/50 focus:border-[#1D8BF8] text-[#0F172A]"
                          >
                            <option value="member">Lid</option>
                            <option value="manager">Manager</option>
                            <option value="admin">Admin</option>
                          </select>
                        </div>
                      </div>
                      <div className="flex gap-3">
                        <button
                          onClick={() => { setShowTeamForm(false); setEditingTeamIndex(null) }}
                          className="px-4 py-2 bg-white text-[#475569] border border-gray-200 rounded-xl font-medium hover:bg-gray-50 transition"
                        >
                          Annuleren
                        </button>
                        <button
                          onClick={handleTeamSave}
                          disabled={loading || !teamFormData.name || !teamFormData.email}
                          className="px-4 py-2 bg-[#3EB489] text-white rounded-xl font-medium hover:bg-[#359E78] transition disabled:opacity-50 inline-flex items-center"
                        >
                          {loading ? (
                            <>
                              <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2" />
                              Opslaan...
                            </>
                          ) : (
                            <>
                              <Save className="w-4 h-4 mr-2" />
                              Opslaan
                            </>
                          )}
                        </button>
                      </div>
                    </div>
                  )}

                  {formData.teamMembers.length > 0 ? (
                    <div className="space-y-4">
                      {formData.teamMembers.map((member, index) => (
                        <div key={index} className="flex items-center justify-between gap-3 p-4 bg-[#F8FAFC] border border-gray-200 rounded-2xl">
                          <div className="flex items-center gap-4 min-w-0 flex-1">
                            <div className="w-12 h-12 rounded-full bg-[#3EB489] flex items-center justify-center flex-shrink-0">
                              <span className="text-white font-bold text-sm">
                                {member.name?.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) || '??'}
                              </span>
                            </div>
                            <div className="min-w-0 flex-1">
                              <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                                <h4 className="font-semibold text-[#0F172A] break-words">{member.name}</h4>
                                <span className={`px-3 py-1 rounded-xl text-xs font-medium whitespace-nowrap ${
                                  member.role === 'admin' ? 'bg-purple-50 text-purple-600 border border-purple-200' :
                                  member.role === 'manager' ? 'bg-blue-50 text-[#1D8BF8] border border-blue-200' :
                                  'bg-gray-50 text-[#475569] border border-gray-200'
                                }`}>
                                  {member.role === 'admin' ? 'Admin' : member.role === 'manager' ? 'Manager' : 'Lid'}
                                </span>
                              </div>
                              <p className="text-sm text-[#475569] truncate">{member.email}</p>
                            </div>
                          </div>
                          <div className="flex gap-2 flex-shrink-0">
                            <button
                              onClick={() => { setEditingTeamIndex(index); setTeamFormData(member); setShowTeamForm(true) }}
                              className="p-2.5 hover:bg-gray-100 rounded-xl transition"
                              aria-label="Teamlid bewerken"
                            >
                              <Edit className="w-4 h-4 text-[#475569]" />
                            </button>
                            <button
                              onClick={() => handleTeamDelete(index)}
                              className="p-2.5 hover:bg-red-50 rounded-xl transition"
                              aria-label="Teamlid verwijderen"
                            >
                              <Trash2 className="w-4 h-4 text-red-400" />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-16">
                      <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-blue-50 flex items-center justify-center">
                        <Users className="w-10 h-10 text-[#1D8BF8]" />
                      </div>
                      <h3 className="text-xl font-bold mb-2 text-[#0F172A]">Nog geen teamleden toegevoegd</h3>
                      <p className="text-[#475569] mb-6 max-w-md mx-auto">
                        Voeg teamleden toe om samen te werken
                      </p>
                    </div>
                  )}
                </div>
              )}

              {/* Billing Tab */}
              {activeTab === 'billing' && (
                <BillingSettings stripeReturn={searchParams.get('stripe')} />
              )}

              {/* Payouts Tab */}
              {activeTab === 'payouts' && user.role === 'creator' && (
                <PayoutSetup />
              )}

              {/* Stats Tab */}
              {activeTab === 'stats' && (
                <div className="text-center py-16">
                  <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-blue-50 flex items-center justify-center">
                    <BarChart3 className="w-10 h-10 text-[#1D8BF8]" />
                  </div>
                  <h3 className="text-2xl font-bold mb-2 text-[#0F172A]">Statistieken komen binnenkort</h3>
                  <p className="text-[#475569] mb-8 max-w-md mx-auto">
                    Hier zie je binnenkort je prestaties en analytics
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Portfolio Modal */}
      {showPortfolioModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-3 sm:p-4">
          <div className="relative max-w-2xl w-full max-h-[90vh] supports-[height:100dvh]:max-h-[calc(100dvh-1.5rem)] overflow-y-auto overscroll-contain">
            <div className="relative bg-white rounded-3xl border border-gray-100 shadow-2xl p-5 sm:p-8">
              <div className="flex justify-between items-center gap-3 mb-6">
                <h3 className="text-xl sm:text-2xl font-bold text-[#0F172A]">
                  {editingPortfolioItem ? 'Portfolio Item Bewerken' : 'Nieuw Portfolio Item'}
                </h3>
                <button
                  onClick={() => {
                    setShowPortfolioModal(false)
                    setEditingPortfolioItem(null)
                    setPortfolioFormData({
                      title: '',
                      description: '',
                      videoUrl: '',
                      thumbnailUrl: '',
                      category: 'other'
                    })
                    setUploadingVideo(false)
                    setUploadingThumbnail(false)
                  }}
                  className="p-2 hover:bg-gray-100 rounded-xl transition text-[#0F172A] flex-shrink-0"
                  aria-label="Sluiten"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              <form onSubmit={handlePortfolioSubmit} className="space-y-6">
                <div>
                  <label className="block text-[11px] font-semibold uppercase tracking-wider text-[#94A3B8] mb-2.5">Titel *</label>
                  <input
                    type="text"
                    value={portfolioFormData.title}
                    onChange={(e) => setPortfolioFormData(prev => ({ ...prev, title: e.target.value }))}
                    className="w-full bg-white border border-gray-200 rounded-xl px-4 py-3 focus:ring-2 focus:ring-[#1D8BF8]/50 focus:border-[#1D8BF8] text-[#0F172A]"
                    placeholder="Bijv: Unboxing video voor tech brand"
                    required
                  />
                </div>

                <div>
                  <label className="block text-[11px] font-semibold uppercase tracking-wider text-[#94A3B8] mb-2.5">Beschrijving</label>
                  <textarea
                    value={portfolioFormData.description}
                    onChange={(e) => setPortfolioFormData(prev => ({ ...prev, description: e.target.value }))}
                    className="w-full bg-white border border-gray-200 rounded-xl px-4 py-3 focus:ring-2 focus:ring-[#1D8BF8]/50 focus:border-[#1D8BF8] text-[#0F172A]"
                    rows={3}
                    placeholder="Beschrijf de video en wat je hebt gedaan..."
                  />
                </div>

                <div>
                  <label className="block text-[11px] font-semibold uppercase tracking-wider text-[#94A3B8] mb-2.5">Video *</label>

                  {/* Video Upload Option */}
                  <div className="space-y-4">
                    <div className="flex gap-4">
                      <label className="flex-1 group relative">
                        <input
                          type="file"
                          accept="video/*"
                          onChange={handleVideoUpload}
                          className="hidden"
                          disabled={uploadingVideo}
                        />
                        <div className="w-full bg-white border border-gray-200 rounded-xl px-4 py-3 hover:bg-gray-100 transition cursor-pointer text-center text-[#0F172A]">
                          {uploadingVideo ? (
                            <div className="flex flex-col items-center gap-1">
                              <div className="flex items-center">
                                <div className="w-4 h-4 border-2 border-gray-300 border-t-[#1D8BF8] rounded-full animate-spin mr-2"></div>
                                Uploaden... {uploadProgress > 0 ? `${uploadProgress}%` : ''}
                              </div>
                              {uploadProgress > 0 && (
                                <div className="w-full bg-gray-200 rounded-full h-1.5">
                                  <div className="bg-[#3EB489] h-1.5 rounded-full transition-all" style={{ width: `${uploadProgress}%` }} />
                                </div>
                              )}
                            </div>
                          ) : (
                            <div className="flex items-center justify-center">
                              <Upload className="w-4 h-4 mr-2" />
                              Video Uploaden
                            </div>
                          )}
                        </div>
                      </label>
                    </div>

                    <div className="text-center text-[#475569]/60 text-sm">of</div>

                    {/* URL Input Option */}
                    <div className="flex gap-2">
                      <input
                        type="url"
                        value={portfolioFormData.videoUrl}
                        onChange={(e) => setPortfolioFormData(prev => ({ ...prev, videoUrl: e.target.value }))}
                        className="flex-1 bg-white border border-gray-200 rounded-xl px-4 py-3 focus:ring-2 focus:ring-[#1D8BF8]/50 focus:border-[#1D8BF8] text-[#0F172A] disabled:opacity-50"
                        placeholder="https://www.youtube.com/watch?v=... (alleen YouTube)"
                        disabled={portfolioFormData.videoUrl && portfolioFormData.videoUrl.includes('/uploads/')}
                        required={!portfolioFormData.videoUrl}
                      />
                      {portfolioFormData.videoUrl && portfolioFormData.videoUrl.includes('/uploads/') && (
                        <button
                          type="button"
                          onClick={() => setPortfolioFormData(prev => ({ ...prev, videoUrl: '' }))}
                          className="px-4 py-3 bg-red-500/20 text-red-400 rounded-xl hover:bg-red-500/30 transition"
                        >
                          Verwijder
                        </button>
                      )}
                    </div>
                    <p className="text-xs text-[#475569]/70 leading-snug">
                      Externe links (zoals Instagram of TikTok) werken niet. Upload je foto's of video's als bestand.
                    </p>
                  </div>
                </div>

                <div>
                  <label className="block text-[11px] font-semibold uppercase tracking-wider text-[#94A3B8] mb-2.5">Thumbnail</label>

                  {/* Show current thumbnail if exists */}
                  {portfolioFormData.thumbnailUrl && (
                    <div className="mb-4 relative">
                      <img
                        src={portfolioFormData.thumbnailUrl}
                        alt="Thumbnail preview"
                        className="w-32 h-20 object-cover rounded-lg border border-gray-200"
                      />
                      {portfolioFormData.thumbnailUrl.includes('/uploads/') && (
                        <button
                          type="button"
                          onClick={() => setPortfolioFormData(prev => ({ ...prev, thumbnailUrl: '' }))}
                          className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600 transition text-xs"
                          title="Thumbnail verwijderen"
                        >
                          ×
                        </button>
                      )}
                    </div>
                  )}

                  <div className="space-y-4">
                    <div className="flex gap-4">
                      <label className="flex-1 group relative">
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleThumbnailUpload}
                          className="hidden"
                          disabled={uploadingThumbnail}
                        />
                        <div className="w-full bg-white border border-gray-200 rounded-xl px-4 py-3 hover:bg-gray-100 transition cursor-pointer text-center text-[#0F172A]">
                          {uploadingThumbnail ? (
                            <div className="flex items-center justify-center">
                              <div className="w-4 h-4 border-2 border-gray-300 border-t-[#1D8BF8] rounded-full animate-spin mr-2"></div>
                              Uploaden...
                            </div>
                          ) : (
                            <div className="flex items-center justify-center">
                              <Upload className="w-4 h-4 mr-2" />
                              Thumbnail Uploaden
                            </div>
                          )}
                        </div>
                      </label>
                    </div>

                    <div className="text-center text-[#475569]/60 text-sm">of</div>

                    <input
                      type="url"
                      value={portfolioFormData.thumbnailUrl}
                      onChange={(e) => setPortfolioFormData(prev => ({ ...prev, thumbnailUrl: e.target.value }))}
                      className="w-full bg-white border border-gray-200 rounded-xl px-4 py-3 focus:ring-2 focus:ring-[#1D8BF8]/50 focus:border-[#1D8BF8] text-[#0F172A] disabled:opacity-50"
                      placeholder="https://example.com/thumbnail.jpg (externe link)"
                      disabled={portfolioFormData.thumbnailUrl && portfolioFormData.thumbnailUrl.includes('/uploads/')}
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-[11px] font-semibold uppercase tracking-wider text-[#94A3B8] mb-2.5">Categorie</label>
                  <select
                    value={portfolioFormData.category}
                    onChange={(e) => setPortfolioFormData(prev => ({ ...prev, category: e.target.value }))}
                    className="w-full bg-white border border-gray-200 rounded-xl px-4 py-3 focus:ring-2 focus:ring-[#1D8BF8]/50 focus:border-[#1D8BF8] text-[#0F172A]"
                  >
                    <option value="unboxing">Unboxing</option>
                    <option value="review">Review</option>
                    <option value="tutorial">Tutorial</option>
                    <option value="lifestyle">Lifestyle</option>
                    <option value="testimonial">Testimonial</option>
                    <option value="other">Anders</option>
                  </select>
                </div>

                <div className="flex gap-4 pt-4">
                  <button
                    type="button"
                    onClick={() => {
                      setShowPortfolioModal(false)
                      setEditingPortfolioItem(null)
                      setPortfolioFormData({
                        title: '',
                        description: '',
                        videoUrl: '',
                        thumbnailUrl: '',
                        category: 'other'
                      })
                      setUploadingVideo(false)
                      setUploadingThumbnail(false)
                    }}
                    className="flex-1 px-6 py-3 bg-[#F8FAFC] text-[#475569] border border-gray-200 rounded-2xl font-medium hover:bg-gray-100 transition"
                  >
                    Annuleren
                  </button>
                  <button
                    type="submit"
                    disabled={loading}
                    className="flex-1 group relative"
                  >
                    <div className="relative px-6 py-3 bg-[#3EB489] text-white rounded-2xl font-semibold hover:bg-[#359E78] transition inline-flex items-center justify-center w-full">
                      {loading ? (
                        <>
                          <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2"></div>
                          Opslaan...
                        </>
                      ) : (
                        <>
                          <Save className="w-4 h-4 mr-2" />
                          {editingPortfolioItem ? 'Bijwerken' : 'Toevoegen'}
                        </>
                      )}
                    </div>
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default ProfilePage